// src/pages/Admin/AdminDashboard.jsx
import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from "react";
import {
  Container,
  Row,
  Col,
  Card,
  Table,
  Badge,
  Spinner,
  Alert,
  Button,
  Modal,
  Form,
  Tab,
  Tabs,
  Dropdown,
  InputGroup,
  Pagination,
  OverlayTrigger,
  Tooltip,
} from "react-bootstrap";
import { Link } from "react-router-dom";
import { toast } from "react-hot-toast";
import { motion } from "framer-motion";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  ArcElement,
  Title,
  Tooltip as ChartTooltip,
  Legend,
  PointElement,
  Filler,
} from "chart.js";
import { Bar, Line, Pie, Doughnut } from "react-chartjs-2";
import axios from "../../api/axios";
import {
  FiPackage,
  FiTruck,
  FiCheckCircle,
  FiUsers,
  FiDollarSign,
  FiTrendingUp,
  FiDownload,
  FiCalendar,
  FiFilter,
  FiRefreshCw,
  FiEye,
  FiEdit,
  FiTrash2,
  FiUserPlus,
  FiShoppingBag,
  FiClock,
  FiAlertCircle,
  FiChevronDown,
  FiChevronLeft,
  FiChevronRight,
  FiFileText,
  FiSearch,
  FiX,
  FiBarChart2,
  FiPieChart,
  FiBox,
  FiMapPin,
  FiMail,
  FiPhone,
  FiHome,
  FiUser,
  FiPlus,
  FiSettings,
  FiBell,
  FiSend,
  FiMoon,
  FiSun,
  FiDatabase,
  FiMessageCircle,
} from "react-icons/fi";

// Enregistrer les composants Chart.js
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  ArcElement,
  Title,
  ChartTooltip,
  Legend,
  PointElement,
  Filler,
);

// ========== CONSTANTES ==========
const ITEMS_PER_PAGE = 10;
const WS_RECONNECT_DELAY = 3000;
const ENABLE_WEBSOCKET = false;
const CHART_COLORS = {
  primary: "#C62828",
  secondary: "#F9A825",
  success: "#10B981",
  info: "#3B82F6",
  warning: "#F59E0B",
  danger: "#EF4444",
  purple: "#8B5CF6",
  pink: "#EC4899",
  indigo: "#6366F1",
};

// ========== FONCTIONS UTILITAIRES GLOBALES ==========
const getPeriodLabel = (period) => {
  const labels = {
    day: "Aujourd'hui",
    week: "Cette semaine",
    month: "Ce mois",
    quarter: "Ce trimestre",
    year: "Cette année",
  };
  return labels[period] || period;
};

const escapeHtml = (text) => {
  if (!text) return "";
  return String(text)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
};

// ========== CARTE STATISTIQUE ==========
const StatCard = ({ icon: Icon, value, label, color, suffix = "" }) => (
  <motion.div
    whileHover={{ y: -4 }}
    transition={{ type: "spring", stiffness: 400 }}
  >
    <Card className="stat-card-modern border-0 shadow-sm h-100">
      <Card.Body className="d-flex align-items-center">
        <div
          className="stat-icon-wrapper me-3"
          style={{ backgroundColor: `${color}15` }}
        >
          <Icon size={24} color={color} />
        </div>
        <div className="flex-grow-1">
          <div className="stat-value" style={{ color }}>
            {typeof value === "number" ? value.toLocaleString() : value}
            {suffix}
          </div>
          <div className="stat-label text-muted">{label}</div>
        </div>
      </Card.Body>
    </Card>
  </motion.div>
);

// ========== COMPOSANT PRINCIPAL ==========
const AdminDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState("");
  const [activeTab, setActiveTab] = useState("overview");
  const [darkMode, setDarkMode] = useState(false);
  const [lastUpdate, setLastUpdate] = useState(new Date());
  const abortControllerRef = useRef(null);
  const wsRef = useRef(null);

  // États des données
  const [parcels, setParcels] = useState([]);
  const [users, setUsers] = useState([]);
  const [validatedOrders, setValidatedOrders] = useState([]);
  const [allOrders, setAllOrders] = useState([]);
  const [whatsappConfigs, setWhatsappConfigs] = useState([]);

  const [notifications, setNotifications] = useState([]);
  const [notificationsLoading, setNotificationsLoading] = useState(false);
  const [notificationsPage, setNotificationsPage] = useState(1);
  const [notificationFilter, setNotificationFilter] = useState("all");
  const [notificationSearch, setNotificationSearch] = useState("");

  // États des filtres
  const [statusFilter, setStatusFilter] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [parcelsPage, setParcelsPage] = useState(1);
  const [ordersPage, setOrdersPage] = useState(1);

  // États des modals
  const [showUserModal, setShowUserModal] = useState(false);
  const [showOrderModal, setShowOrderModal] = useState(false);
  const [showWhatsappModal, setShowWhatsappModal] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);

  // États des formulaires
  const [userForm, setUserForm] = useState({
    email: "",
    first_name: "",
    last_name: "",
    phone: "",
    role: "operator",
    password: "",
  });
  const [whatsappForm, setWhatsappForm] = useState({
    phone_number: "",
    label: "",
    is_default: false,
    is_active: true,
  });
  const [reportPeriod, setReportPeriod] = useState("month");
  const [reportFormat, setReportFormat] = useState("pdf");

  // États de chargement
  const [userLoading, setUserLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);

  // ========== WEBSOCKET CONNECTION (DÉSACTIVÉ) ==========
  const setupWebSocket = useCallback(() => {
    if (!ENABLE_WEBSOCKET) {
      return;
    }

    if (wsRef.current?.readyState === WebSocket.OPEN) return;

    const protocol = window.location.protocol === "https:" ? "wss:" : "ws:";
    const wsUrl = `${protocol}//${window.location.host}/ws/stats/`;

    try {
      wsRef.current = new WebSocket(wsUrl);

      wsRef.current.onopen = () => {
      };

      wsRef.current.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          handleWebSocketMessage(data);
        } catch (err) {
          console.error("Erreur parsing WebSocket:", err);
        }
      };

      wsRef.current.onerror = (error) => {
        console.error("WebSocket error:", error);
      };

      wsRef.current.onclose = () => {
        setTimeout(setupWebSocket, WS_RECONNECT_DELAY);
      };
    } catch (err) {
      console.error("Erreur connexion WebSocket:", err);
    }
  }, []);

  const handleWebSocketMessage = useCallback((data) => {
    switch (data.type) {
      case "new_parcel":
        setParcels((prev) => [data.parcel, ...prev]);
        toast.success(`📦 Nouveau colis: ${data.parcel.tracking_number}`);
        break;
      case "status_update":
        setParcels((prev) =>
          prev.map((p) =>
            p.id === data.parcel_id ? { ...p, current_status: data.status } : p,
          ),
        );
        toast.info(`📦 Colis #${data.tracking_number}: ${data.status_label}`);
        break;
      case "new_notification":
        setNotifications((prev) => [data.notification, ...prev]);
        toast.info(`🔔 ${data.notification.title}`);
        break;
      case "stats_update":
        break;
      default:
    }
  }, []);

  // ========== FETCH DATA ==========
  const fetchAllData = useCallback(async () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    abortControllerRef.current = new AbortController();

    try {
      setLoading(true);
      setError("");
      await Promise.all([
        fetchParcels(),
        fetchUsers(),
        fetchOrders(),
        fetchValidatedOrders(),
        fetchWhatsappConfigs(),
        fetchNotifications(),
      ]);
      setLastUpdate(new Date());
    } catch (err) {
      if (err.name !== "AbortError") {
        console.error("Erreur chargement:", err);
        setError("Erreur lors du chargement des données");
      }
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchParcels = async () => {
    try {
      const response = await axios.get("/parcels/?all=true", {
        signal: abortControllerRef.current?.signal,
      });

      let data = [];
      if (Array.isArray(response.data)) {
        data = response.data;
      } else if (response.data?.results) {
        data = response.data.results;
      } else {
        data = response.data;
      }

      setParcels(Array.isArray(data) ? data : []);
      return data;
    } catch (err) {
      if (err.name === "AbortError") return [];

      console.error("Erreur chargement colis:", err);

      try {
        let allParcels = [];
        let nextUrl = "/parcels/";
        let pageCount = 0;

        while (nextUrl) {
          const response = await axios.get(nextUrl, {
            signal: abortControllerRef.current?.signal,
          });
          const data = response.data;

          if (data.results) {
            allParcels = [...allParcels, ...data.results];
            nextUrl = data.next;
            pageCount++;
          } else if (Array.isArray(data)) {
            allParcels = [...allParcels, ...data];
            break;
          } else {
            break;
          }
        }

        setParcels(allParcels);
        return allParcels;
      } catch (fallbackErr) {
        if (fallbackErr.name !== "AbortError") {
          console.error("Erreur fallback:", fallbackErr);
        }
        setParcels([]);
        return [];
      }
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await axios.get("/auth/users/", {
        signal: abortControllerRef.current?.signal,
      });
      const data = response.data || [];
      setUsers(Array.isArray(data) ? data : []);
    } catch (err) {
      if (err.name !== "AbortError") {
        console.error("Erreur utilisateurs:", err);
      }
      setUsers([]);
    }
  };

  const fetchOrders = async () => {
    try {
      const response = await axios.get("/store/orders/admin/all/", {
        signal: abortControllerRef.current?.signal,
      });
      const data = response.data || [];
      setAllOrders(Array.isArray(data) ? data : []);
    } catch (err) {
      if (err.name !== "AbortError") {
        console.error("Erreur commandes:", err);
      }
      setAllOrders([]);
    }
  };

  const fetchValidatedOrders = async () => {
    try {
      const response = await axios.get("/store/orders/validated/", {
        signal: abortControllerRef.current?.signal,
      });
      const data = response.data || [];
      setValidatedOrders(Array.isArray(data) ? data : []);
    } catch (err) {
      if (err.name !== "AbortError") {
        console.error("Erreur commandes validées:", err);
      }
      setValidatedOrders([]);
    }
  };

  const fetchWhatsappConfigs = async () => {
    try {
      const response = await axios.get("/whatsapp/config/", {
        signal: abortControllerRef.current?.signal,
      });

      let normalizedData = [];

      if (Array.isArray(response.data)) {
        normalizedData = response.data;
      } else if (
        response.data?.results &&
        Array.isArray(response.data.results)
      ) {
        normalizedData = response.data.results;
      } else if (response.data && typeof response.data === "object") {
        if (response.data.id || response.data.phone_number) {
          normalizedData = [response.data];
        } else {
          normalizedData = Object.values(response.data).filter(
            (v) => typeof v === "object",
          );
        }
      }

      setWhatsappConfigs(normalizedData);
    } catch (err) {
      if (err.name !== "AbortError") {
        console.error("Erreur WhatsApp:", err);
      }
      setWhatsappConfigs([]);
    }
  };

  const normalizeNotification = (notif) => ({
    ...notif,
    whatsapp_sent: notif.whatsapp_sent ?? false,
    whatsapp_error: notif.whatsapp_error ?? "",
    whatsapp_sent_at: notif.whatsapp_sent_at ?? null,
    email_sent: notif.email_sent ?? true,
    email_error: notif.email_error ?? "",
    channels: notif.channels ?? ["in_app", "email"],
    recipient_phone: notif.recipient_phone ?? "",
    priority: notif.priority ?? "normal",
    is_read: notif.is_read ?? false,
  });

  const fetchNotifications = async () => {
    setNotificationsLoading(true);
    try {
      let response;
      try {
        response = await axios.get("/notifications/admin/all/?limit=500", {
          signal: abortControllerRef.current?.signal,
        });
      } catch {
        response = await axios.get("/notifications/?limit=500", {
          signal: abortControllerRef.current?.signal,
        });
      }
      let data = [];
      if (Array.isArray(response.data)) data = response.data;
      else if (response.data?.results) data = response.data.results;

      const normalizedData = data.map(normalizeNotification);
      setNotifications(normalizedData);
    } catch (err) {
      if (err.name !== "AbortError") {
        console.error("Erreur notifications:", err);
      }
      setNotifications([]);
    } finally {
      setNotificationsLoading(false);
    }
  };

  // ========== INITIALISATION ==========
  useEffect(() => {
    fetchAllData();
    setupWebSocket();

    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, [fetchAllData, setupWebSocket]);

  // Rafraîchissement automatique
  useEffect(() => {
    const interval = setInterval(() => {
      if (activeTab !== "overview") return;
      fetchAllData();
    }, 30000);
    return () => clearInterval(interval);
  }, [activeTab, fetchAllData]);

  // Mode sombre
  useEffect(() => {
    if (darkMode) {
      document.body.classList.add("dark-mode");
    } else {
      document.body.classList.remove("dark-mode");
    }
  }, [darkMode]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchAllData();
    setRefreshing(false);
    toast.success("✅ Données actualisées");
  };

  // ========== STATISTIQUES CALCULÉES ==========
  const stats = useMemo(() => {
    const operators = users.filter((u) => u.role === "operator");
    const admins = users.filter((u) => u.role === "admin");
    const clients = users.filter((u) => u.role === "client");

    const inTransit = parcels.filter((p) =>
      ["transit_air", "transit_sea"].includes(p.current_status),
    ).length;
    const delivered = parcels.filter(
      (p) => p.current_status === "delivered",
    ).length;
    const incident = parcels.filter(
      (p) => p.current_status === "incident",
    ).length;

    const totalRevenue = parcels.reduce(
      (sum, p) => sum + (parseFloat(p.total_cost) || 0),
      0,
    );
    const storeRevenue = allOrders.reduce(
      (sum, o) => sum + (parseFloat(o.total_amount) || 0),
      0,
    );

    let avgDeliveryDays = 0;
    const deliveredParcels = parcels.filter(
      (p) => p.current_status === "delivered" && p.created_at,
    );
    if (deliveredParcels.length > 0) {
      const totalDays = deliveredParcels.reduce((sum, p) => {
        const days = Math.ceil(
          (new Date(p.updated_at || p.created_at) - new Date(p.created_at)) /
            (1000 * 60 * 60 * 24),
        );
        return sum + days;
      }, 0);
      avgDeliveryDays = Math.round(totalDays / deliveredParcels.length);
    }

    return {
      totalParcels: parcels.length,
      inTransit,
      delivered,
      incident,
      totalRevenue,
      storeRevenue,
      totalOrders: allOrders.length,
      validatedOrders: validatedOrders.length,
      operators: operators.length,
      admins: admins.length,
      clients: clients.length,
      avgDeliveryDays,
    };
  }, [parcels, users, allOrders, validatedOrders]);

  // ========== DONNÉES POUR GRAPHIQUES ==========
  const chartData = useMemo(() => {
    const defaultStatusCounts = {
      registered: 0,
      transit: 0,
      arrived: 0,
      delivering: 0,
      delivered: 0,
      incident: 0,
    };
    const defaultShipmentTypes = { air: 0, sea: 0 };
    const defaultMonths = ["Jan", "Fév", "Mar", "Avr", "Mai", "Juin"];

    if (!parcels || parcels.length === 0) {
      return {
        months: defaultMonths,
        parcelsData: [0, 0, 0, 0, 0, 0],
        revenueData: [0, 0, 0, 0, 0, 0],
        statusCounts: defaultStatusCounts,
        shipmentTypes: defaultShipmentTypes,
      };
    }

    const monthlyParcels = {};
    const monthlyRevenue = {};

    parcels.forEach((p) => {
      if (p.created_at) {
        const month = new Date(p.created_at).toLocaleDateString("fr-FR", {
          month: "short",
          year: "numeric",
        });
        monthlyParcels[month] = (monthlyParcels[month] || 0) + 1;
        monthlyRevenue[month] =
          (monthlyRevenue[month] || 0) + (parseFloat(p.total_cost) || 0);
      }
    });

    const months =
      Object.keys(monthlyParcels).length > 0
        ? Object.keys(monthlyParcels).slice(-6)
        : defaultMonths;

    const statusCounts = {
      registered: parcels.filter((p) => p.current_status === "registered")
        .length,
      transit: parcels.filter((p) =>
        ["transit_air", "transit_sea"].includes(p.current_status),
      ).length,
      arrived: parcels.filter((p) => p.current_status === "arrived").length,
      delivering: parcels.filter((p) => p.current_status === "delivering")
        .length,
      delivered: parcels.filter((p) => p.current_status === "delivered").length,
      incident: parcels.filter((p) => p.current_status === "incident").length,
    };

    const shipmentTypes = {
      air: parcels.filter((p) => p.shipment_type === "air").length,
      sea: parcels.filter((p) => p.shipment_type === "sea").length,
    };

    return {
      months,
      parcelsData: months.map((m) => monthlyParcels[m] || 0),
      revenueData: months.map((m) => monthlyRevenue[m] || 0),
      statusCounts,
      shipmentTypes,
    };
  }, [parcels]);

  const barChartConfig = {
    labels: chartData.months,
    datasets: [
      {
        label: "Nombre de colis",
        data: chartData.parcelsData,
        backgroundColor: CHART_COLORS.primary + "80",
        borderColor: CHART_COLORS.primary,
        borderWidth: 2,
        borderRadius: 8,
      },
    ],
  };

  const lineChartConfig = {
    labels: chartData.months,
    datasets: [
      {
        label: "Chiffre d'affaires (FCFA)",
        data: chartData.revenueData,
        borderColor: CHART_COLORS.success,
        backgroundColor: CHART_COLORS.success + "20",
        borderWidth: 3,
        fill: true,
        tension: 0.4,
        pointBackgroundColor: CHART_COLORS.success,
        pointBorderColor: "white",
        pointBorderWidth: 2,
        pointRadius: 6,
        pointHoverRadius: 8,
      },
    ],
  };

  const pieChartConfig = {
    labels: [
      "Enregistré",
      "En transit",
      "Arrivé",
      "En livraison",
      "Livré",
      "Incident",
    ],
    datasets: [
      {
        data: [
          chartData.statusCounts.registered || 0,
          chartData.statusCounts.transit || 0,
          chartData.statusCounts.arrived || 0,
          chartData.statusCounts.delivering || 0,
          chartData.statusCounts.delivered || 0,
          chartData.statusCounts.incident || 0,
        ],
        backgroundColor: [
          "#64748B",
          "#3B82F6",
          "#F59E0B",
          "#8B5CF6",
          "#10B981",
          "#EF4444",
        ],
        borderWidth: 0,
      },
    ],
  };

  const doughnutChartConfig = {
    labels: ["Aérien", "Maritime"],
    datasets: [
      {
        data: [
          chartData.shipmentTypes.air || 0,
          chartData.shipmentTypes.sea || 0,
        ],
        backgroundColor: [CHART_COLORS.info, CHART_COLORS.warning],
        borderWidth: 0,
      },
    ],
  };

  const usersChartConfig = {
    labels: chartData.months,
    datasets: [
      {
        label: "Nouveaux clients",
        data: [12, 19, 15, 17, 14, 23],
        borderColor: CHART_COLORS.purple,
        backgroundColor: CHART_COLORS.purple + "20",
        borderWidth: 2,
        fill: true,
        tension: 0.4,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: darkMode ? "#1E293B" : "#1E293B",
        titleColor: "#F8FAFC",
        bodyColor: "#E2E8F0",
        padding: 12,
        cornerRadius: 8,
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        grid: { color: darkMode ? "#334155" : "#E2E8F0" },
      },
      x: {
        grid: { display: false },
      },
    },
  };

  // ========== FILTRES ==========
  const filteredParcels = useMemo(() => {
    if (!parcels || parcels.length === 0) return [];

    let filtered = [...parcels];

    if (statusFilter !== "all") {
      if (statusFilter === "transit") {
        filtered = filtered.filter((p) =>
          ["transit_air", "transit_sea"].includes(p.current_status),
        );
      } else {
        filtered = filtered.filter((p) => p.current_status === statusFilter);
      }
    }

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (p) =>
          p.tracking_number?.toLowerCase().includes(term) ||
          p.recipient_name?.toLowerCase().includes(term) ||
          p.sender_name?.toLowerCase().includes(term),
      );
    }

    return filtered.sort(
      (a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0),
    );
  }, [parcels, statusFilter, searchTerm]);

  const paginatedParcels = useMemo(() => {
    const start = (parcelsPage - 1) * ITEMS_PER_PAGE;
    return filteredParcels.slice(start, start + ITEMS_PER_PAGE);
  }, [filteredParcels, parcelsPage]);

  const totalParcelPages = Math.ceil(filteredParcels.length / ITEMS_PER_PAGE);

  const filteredOrders = useMemo(() => {
    if (!allOrders || allOrders.length === 0) return [];
    return [...allOrders].sort(
      (a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0),
    );
  }, [allOrders]);

  const paginatedOrders = useMemo(() => {
    const start = (ordersPage - 1) * ITEMS_PER_PAGE;
    return filteredOrders.slice(start, start + ITEMS_PER_PAGE);
  }, [filteredOrders, ordersPage]);

  const totalOrderPages = Math.ceil(filteredOrders.length / ITEMS_PER_PAGE);

  const filteredNotifications = useMemo(() => {
    let filtered = [...notifications];

    if (notificationFilter !== "all") {
      if (notificationFilter === "whatsapp_sent") {
        filtered = filtered.filter((n) => n.whatsapp_sent === true);
      } else if (notificationFilter === "whatsapp_failed") {
        filtered = filtered.filter(
          (n) => n.whatsapp_error && n.whatsapp_error !== "",
        );
      } else if (notificationFilter === "whatsapp_pending") {
        filtered = filtered.filter(
          (n) =>
            n.channels?.includes("whatsapp") &&
            !n.whatsapp_sent &&
            !n.whatsapp_error,
        );
      } else {
        filtered = filtered.filter(
          (n) => n.notification_type === notificationFilter,
        );
      }
    }

    if (notificationSearch) {
      const term = notificationSearch.toLowerCase();
      filtered = filtered.filter(
        (n) =>
          n.title?.toLowerCase().includes(term) ||
          n.message?.toLowerCase().includes(term) ||
          (n.recipient_email || "").toLowerCase().includes(term) ||
          (n.recipient_phone || "").toLowerCase().includes(term),
      );
    }

    return filtered.sort(
      (a, b) => new Date(b.created_at) - new Date(a.created_at),
    );
  }, [notifications, notificationFilter, notificationSearch]);

  const paginatedNotifications = useMemo(() => {
    const start = (notificationsPage - 1) * ITEMS_PER_PAGE;
    return filteredNotifications.slice(start, start + ITEMS_PER_PAGE);
  }, [filteredNotifications, notificationsPage]);

  const totalNotificationPages = Math.ceil(
    filteredNotifications.length / ITEMS_PER_PAGE,
  );

  // ========== FONCTIONS UTILITAIRES ==========
  const getStatusBadge = (status) => {
    const config = {
      registered: { text: "📝 Enregistré", variant: "secondary" },
      transit_air: { text: "✈️ Transit aérien", variant: "primary" },
      transit_sea: { text: "🚢 Transit maritime", variant: "primary" },
      arrived: { text: "📦 Arrivé", variant: "warning" },
      delivering: { text: "🚚 En livraison", variant: "info" },
      delivered: { text: "✅ Livré", variant: "success" },
      incident: { text: "⚠️ Incident", variant: "danger" },
    };
    return config[status] || { text: status, variant: "secondary" };
  };

  const getOrderStatusBadge = (status) => {
    const config = {
      pending: { text: "🟡 En attente", variant: "warning" },
      validated: { text: "🟢 Validée", variant: "success" },
      refused: { text: "🔴 Refusée", variant: "danger" },
      shipped: { text: "📦 Expédiée", variant: "info" },
      delivered: { text: "✅ Livrée", variant: "secondary" },
      cancelled: { text: "❌ Annulée", variant: "secondary" },
    };
    return config[status] || { text: status, variant: "secondary" };
  };

  const getNotificationTypeBadge = (type) => {
    const config = {
      order: { text: "🛍️ Commande", variant: "warning" },
      parcel: { text: "📦 Colis", variant: "info" },
      payment: { text: "💳 Paiement", variant: "success" },
      system: { text: "⚙️ Système", variant: "secondary" },
      message: { text: "💬 Message", variant: "primary" },
      alert: { text: "🚨 Alerte", variant: "danger" },
      promo: { text: "🎉 Promotion", variant: "success" },
    };
    return config[type] || { text: type, variant: "secondary" };
  };

  const getPriorityBadge = (priority) => {
    const config = {
      low: { text: "🟢 Basse", variant: "success" },
      normal: { text: "🔵 Normale", variant: "primary" },
      high: { text: "🟠 Haute", variant: "warning" },
      urgent: { text: "🔴 Urgente", variant: "danger" },
    };
    return config[priority] || { text: priority, variant: "secondary" };
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleDateString("fr-FR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatShortDate = (dateString) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleDateString("fr-FR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  const formatPrice = (price) => (parseFloat(price) || 0).toLocaleString();

  // ========== GESTION DES EXPORTS ==========
  const handleExport = async (type, format) => {
    setExportLoading(true);
    try {
      let endpoint = "";
      let filename = "";

      if (type === "parcels") {
        endpoint = `parcels/export/?format=${format}`;
        filename = `colis_${new Date().toISOString().split("T")[0]}`;
      } else if (type === "orders") {
        endpoint = `store/orders/export/?format=${format}`;
        filename = `commandes_${new Date().toISOString().split("T")[0]}`;
      } else if (type === "report") {
        endpoint = `reports/export/?period=${reportPeriod}&format=${format}`;
        filename = `rapport_${reportPeriod}_${new Date().toISOString().split("T")[0]}`;
      } else if (type === "notifications") {
        exportNotificationsCSV();
        setExportLoading(false);
        return;
      }

      if (format === "xlsx" || format === "excel") {
        if (type === "report") {
          exportReportExcel(filename);
        } else if (type === "parcels") {
          exportParcelsExcel(filename);
        } else if (type === "orders") {
          exportOrdersExcel(filename);
        }
        setExportLoading(false);
        return;
      }

      if (format === "pdf") {
        exportAsPDF(type);
        setExportLoading(false);
        return;
      }

      try {
        const response = await axios.get(endpoint, {
          responseType: "blob",
          timeout: 30000,
        });

        const blob = new Blob([response.data], {
          type: "text/csv; charset=utf-8-sig",
        });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", filename + ".csv");
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);

        toast.success(`✅ Export CSV réussi`);
      } catch (apiErr) {

        if (type === "report") {
          exportReportCSV();
        } else if (type === "parcels") {
          exportParcelsCSV();
        } else if (type === "orders") {
          exportOrdersCSV();
        }
      }
    } catch (err) {
      console.error("Erreur export:", err);
      toast.error("Erreur lors de l'export");
    } finally {
      setExportLoading(false);
    }
  };

  const exportNotificationsCSV = () => {
    const filename = `notifications_${new Date().toISOString().split("T")[0]}.csv`;

    let csv = "\ufeff";
    csv +=
      "Date;Type;Titre;Message;Destinataire;Email;Téléphone;Priorité;WhatsApp;Email;Statut\n";

    filteredNotifications.forEach((n) => {
      csv += `${formatDate(n.created_at)};`;
      csv += `${n.notification_type};`;
      csv += `"${n.title.replace(/"/g, '""')}";`;
      csv += `"${n.message?.replace(/"/g, '""') || ""}";`;
      csv += `${n.recipient_name || "-"};`;
      csv += `${n.recipient_email || "-"};`;
      csv += `${n.recipient_phone || "-"};`;
      csv += `${n.priority};`;
      csv += `${n.whatsapp_sent ? "Envoyé" : n.whatsapp_error ? "Échec" : "Non envoyé"};`;
      csv += `${n.email_sent ? "Envoyé" : n.email_error ? "Échec" : "Non envoyé"};`;
      csv += `${n.is_read ? "Lu" : "Non lu"}\n`;
    });

    downloadFile(csv, filename, "text/csv; charset=utf-8-sig");
    toast.success(`✅ ${filteredNotifications.length} notifications exportées`);
  };

  const exportReportExcel = (filename) => {
    let html = `
        <html xmlns:o="urn:schemas-microsoft-com:office:office" 
              xmlns:x="urn:schemas-microsoft-com:office:excel" 
              xmlns="http://www.w3.org/TR/REC-html40">
        <head>
            <meta charset="UTF-8">
            <style>
                body { font-family: 'Calibri', Arial, sans-serif; font-size: 11pt; }
                .title { font-size: 18pt; font-weight: bold; color: #C62828; text-align: center; padding: 10px; }
                .subtitle { font-size: 12pt; color: #666; text-align: center; padding: 5px; }
                .section-title { font-size: 14pt; font-weight: bold; color: white; background-color: #C62828; padding: 8px; margin-top: 15px; }
                .stats-table { border-collapse: collapse; width: 100%; margin: 10px 0; }
                .stats-table th { background-color: #C62828; color: white; padding: 8px 12px; border: 1px solid #B71C1C; font-weight: bold; text-align: left; }
                .stats-table td { padding: 8px 12px; border: 1px solid #ddd; }
                .stats-table tr:nth-child(even) { background-color: #FFF8E1; }
                .data-table { border-collapse: collapse; width: 100%; margin: 10px 0; font-size: 10pt; }
                .data-table th { background-color: #1E293B; color: white; padding: 6px 8px; border: 1px solid #0F172A; font-weight: bold; text-align: center; white-space: nowrap; }
                .data-table td { padding: 5px 8px; border: 1px solid #ddd; }
                .data-table tr:nth-child(even) { background-color: #F8FAFC; }
                .footer { text-align: center; color: #999; font-size: 9pt; margin-top: 20px; border-top: 1px solid #ddd; padding-top: 10px; }
            </style>
        </head>
        <body>
            <div class="title">📊 RAPPORT CAC - CHINE AFRIQUE CARGO</div>
            <div class="subtitle">Période : ${getPeriodLabel(reportPeriod)} | Généré le ${new Date().toLocaleDateString("fr-FR", { day: "2-digit", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit" })}</div>
            
            <div class="section-title">📈 STATISTIQUES GÉNÉRALES</div>
            <table class="stats-table">
                <tr><th>Indicateur</th><th>Valeur</th></tr>
                <tr><td>Total colis enregistrés</td><td><strong>${stats.totalParcels}</strong></td></tr>
                <tr class="success"><td>Colis livrés avec succès</td><td><strong>${stats.delivered}</strong></td></tr>
                <tr><td>Colis en transit</td><td><strong>${stats.inTransit}</strong></td></tr>
                <tr class="danger"><td>Incidents détectés</td><td><strong>${stats.incident}</strong></td></tr>
                <tr class="warning"><td>Chiffre d'affaires colis</td><td><strong>${stats.totalRevenue.toLocaleString()} FCFA</strong></td></tr>
                <tr><td>Total commandes store</td><td><strong>${stats.totalOrders}</strong></td></tr>
                <tr class="success"><td>Commandes validées</td><td><strong>${stats.validatedOrders}</strong></td></tr>
                <tr><td>Chiffre d'affaires store</td><td><strong>${stats.storeRevenue.toLocaleString()} FCFA</strong></td></tr>
                <tr><td>Délai moyen de livraison</td><td><strong>${stats.avgDeliveryDays} jours</strong></td></tr>
            </table>
            
            <div class="section-title">👥 RESSOURCES HUMAINES</div>
            <table class="stats-table">
                <tr><th>Rôle</th><th>Nombre</th></tr>
                <tr><td>👥 Opérateurs</td><td><strong>${stats.operators}</strong></td></tr>
                <tr><td>👑 Administrateurs</td><td><strong>${stats.admins}</strong></td></tr>
                <tr><td>🛒 Clients</td><td><strong>${stats.clients}</strong></td></tr>
            </table>
            
            <div class="section-title">📦 DÉTAIL DES COLIS (${parcels.length})</div>
            <table class="data-table">
                <thead>
                    <tr>
                        <th>N° Suivi</th><th>Destinataire</th><th>Expéditeur</th>
                        <th>Statut</th><th>Type</th><th>Poids (kg)</th><th>Volume (CBM)</th>
                        <th>Montant (FCFA)</th><th>Date création</th><th>Livraison prévue</th>
                    </tr>
                </thead>
                <tbody>
        `;

    parcels.slice(0, 500).forEach((p) => {
      const statusConfig = getStatusBadge(p.current_status);
      html += `
                <tr>
                    <td style="font-family: monospace;">${escapeHtml(p.tracking_number)}</td>
                    <td>${escapeHtml(p.recipient_name)}</td>
                    <td>${escapeHtml(p.sender_name)}</td>
                    <td>${statusConfig.text}</td>
                    <td>${p.shipment_type === "air" ? "✈️ Aérien" : "🚢 Maritime"}</td>
                    <td style="text-align: center;">${p.weight_kg || "-"}</td>
                    <td style="text-align: center;">${p.volume_cbm || "-"}</td>
                    <td style="text-align: right; font-weight: bold;">${(p.total_cost || 0).toLocaleString()}</td>
                    <td style="text-align: center;">${formatShortDate(p.created_at)}</td>
                    <td style="text-align: center;">${formatShortDate(p.expected_delivery_date)}</td>
                </tr>
            `;
    });

    html += `
                </tbody>
            </table>
            
            <div class="footer">
                © ${new Date().getFullYear()} CAC - Chine Afrique Cargo | Rapport généré automatiquement
            </div>
        </body>
        </html>
        `;

    downloadFile(
      html,
      `${filename}.xls`,
      "application/vnd.ms-excel; charset=utf-8",
    );
    toast.success(`✅ Rapport Excel exporté`);
  };

  const exportParcelsExcel = (filename) => {
    let html = `
        <html xmlns:o="urn:schemas-microsoft-com:office:office" 
              xmlns:x="urn:schemas-microsoft-com:office:excel" 
              xmlns="http://www.w3.org/TR/REC-html40">
        <head>
            <meta charset="UTF-8">
            <style>
                body { font-family: 'Calibri', Arial; font-size: 11pt; }
                .title { font-size: 16pt; font-weight: bold; color: #C62828; text-align: center; padding: 10px; }
                .subtitle { font-size: 10pt; color: #666; text-align: center; }
                table { border-collapse: collapse; width: 100%; margin: 10px 0; }
                th { background-color: #C62828; color: white; padding: 8px; border: 1px solid #B71C1C; text-align: center; }
                td { padding: 6px 8px; border: 1px solid #ddd; }
                .footer { text-align: center; color: #999; font-size: 9pt; margin-top: 20px; }
            </style>
        </head>
        <body>
            <div class="title">📦 EXPORT DES COLIS</div>
            <div class="subtitle">Exporté le ${new Date().toLocaleDateString("fr-FR")} | ${parcels.length} colis</div>
            <table>
                <thead>
                    <tr>
                        <th>N° Suivi</th><th>Destinataire</th><th>Expéditeur</th><th>Description</th>
                        <th>Statut</th><th>Type</th><th>Poids (kg)</th><th>Volume (CBM)</th>
                        <th>Montant (FCFA)</th><th>Date création</th>
                    </tr>
                </thead>
                <tbody>
        `;

    parcels.forEach((p) => {
      html += `
                <tr>
                    <td>${escapeHtml(p.tracking_number)}</td>
                    <td>${escapeHtml(p.recipient_name)}</td>
                    <td>${escapeHtml(p.sender_name)}</td>
                    <td>${escapeHtml((p.description || "").substring(0, 60))}</td>
                    <td>${getStatusBadge(p.current_status).text}</td>
                    <td>${p.shipment_type === "air" ? "✈️ Aérien" : "🚢 Maritime"}</td>
                    <td style="text-align: center;">${p.weight_kg || "-"}</td>
                    <td style="text-align: center;">${p.volume_cbm || "-"}</td>
                    <td style="text-align: right; font-weight: bold;">${(p.total_cost || 0).toLocaleString()}</td>
                    <td style="text-align: center;">${formatShortDate(p.created_at)}</td>
                </tr>
            `;
    });

    html += `
                </tbody>
            </table>
            <div class="footer">© ${new Date().getFullYear()} CAC - Chine Afrique Cargo</div>
        </body>
        </html>
        `;

    downloadFile(
      html,
      `${filename}.xls`,
      "application/vnd.ms-excel; charset=utf-8",
    );
    toast.success(`✅ ${parcels.length} colis exportés en Excel`);
  };

  const exportOrdersExcel = (filename) => {
    let html = `
        <html xmlns:o="urn:schemas-microsoft-com:office:office" 
              xmlns:x="urn:schemas-microsoft-com:office:excel" 
              xmlns="http://www.w3.org/TR/REC-html40">
        <head>
            <meta charset="UTF-8">
            <style>
                body { font-family: 'Calibri', Arial; font-size: 11pt; }
                .title { font-size: 16pt; font-weight: bold; color: #C62828; text-align: center; padding: 10px; }
                .subtitle { font-size: 10pt; color: #666; text-align: center; }
                table { border-collapse: collapse; width: 100%; margin: 10px 0; }
                th { background-color: #C62828; color: white; padding: 8px; border: 1px solid #B71C1C; text-align: center; }
                td { padding: 6px 8px; border: 1px solid #ddd; }
                .footer { text-align: center; color: #999; font-size: 9pt; margin-top: 20px; }
            </style>
        </head>
        <body>
            <div class="title">🛍️ EXPORT DES COMMANDES</div>
            <div class="subtitle">Exporté le ${new Date().toLocaleDateString("fr-FR")} | ${allOrders.length} commandes</div>
            <table>
                <thead>
                    <tr>
                        <th>N° Commande</th><th>Client</th><th>Email</th><th>Téléphone</th>
                        <th>Montant (FCFA)</th><th>Statut</th><th>Paiement</th><th>Date commande</th>
                    </tr>
                </thead>
                <tbody>
        `;

    allOrders.forEach((o) => {
      html += `
                <tr>
                    <td>${escapeHtml(o.order_number)}</td>
                    <td>${escapeHtml(o.shipping_name)}</td>
                    <td>${o.client_email || "-"}</td>
                    <td>${o.shipping_phone || "-"}</td>
                    <td style="text-align: right;">${(o.total_amount || 0).toLocaleString()}</td>
                    <td>${getOrderStatusBadge(o.status).text}</td>
                    <td>${o.is_paid ? "✅ Payé" : "⏳ En attente"}</td>
                    <td style="text-align: center;">${formatShortDate(o.created_at)}</td>
                </tr>
            `;
    });

    html += `
                </tbody>
            </table>
            <div class="footer">© ${new Date().getFullYear()} CAC - Chine Afrique Cargo</div>
        </body>
        </html>
        `;

    downloadFile(
      html,
      `${filename}.xls`,
      "application/vnd.ms-excel; charset=utf-8",
    );
    toast.success(`✅ ${allOrders.length} commandes exportées en Excel`);
  };

  const exportAsPDF = (type) => {
    toast.success("🖨️ Ouverture de l'aperçu d'impression...");

    const printWindow = window.open("", "_blank", "width=800,height=600");

    const content = `
        <div style="padding: 20px; font-family: Arial, sans-serif;">
            <h1 style="color: #C62828;">CAC - Chine Afrique Cargo</h1>
            <h2>${type === "report" ? "Rapport d'export" : type === "parcels" ? "Export des colis" : "Export des commandes"}</h2>
            <p><strong>Généré le:</strong> ${new Date().toLocaleDateString("fr-FR", { day: "2-digit", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit" })}</p>
            <hr/>
            <h3>Statistiques</h3>
            <table border="1" cellpadding="5" cellspacing="0" style="width:100%; border-collapse: collapse;">
                <tr style="background-color: #C62828; color: white;">
                    <th>Indicateur</th><th>Valeur</th>
                </tr>
                <tr><td><strong>Total colis</strong></td><td>${stats.totalParcels}</td></tr>
                <tr><td><strong>Livrés</strong></td><td>${stats.delivered}</td></tr>
                <tr><td><strong>En transit</strong></td><td>${stats.inTransit}</td></tr>
                <tr><td><strong>Incidents</strong></td><td>${stats.incident}</td></tr>
                <tr><td><strong>CA Colis</strong></td><td>${stats.totalRevenue.toLocaleString()} FCFA</td></tr>
                <tr><td><strong>Commandes</strong></td><td>${stats.totalOrders}</td></tr>
                <tr><td><strong>CA Store</strong></td><td>${stats.storeRevenue.toLocaleString()} FCFA</td></tr>
                <tr><td><strong>Délai moyen</strong></td><td>${stats.avgDeliveryDays} jours</td></tr>
            </table>
        </div>
        `;

    printWindow.document.write(`
        <!DOCTYPE html>
        <html>
        <head>
            <title>Export CAC</title>
            <style>
                body { font-family: Arial, sans-serif; padding: 20px; }
                h1 { color: #C62828; }
                table { width: 100%; border-collapse: collapse; margin: 20px 0; }
                th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
                th { background-color: #C62828; color: white; }
                @media print {
                    body { print-color-adjust: exact; -webkit-print-color-adjust: exact; }
                }
            </style>
        </head>
        <body>
            ${content}
            <script>
                window.onload = function() {
                    window.print();
                    setTimeout(function() { window.close(); }, 500);
                };
            </script>
        </body>
        </html>
        `);

    printWindow.document.close();
  };

  const exportReportCSV = () => {
    const filename = `rapport_${reportPeriod}_${new Date().toISOString().split("T")[0]}.csv`;

    let csv = "\ufeff";
    csv += "RAPPORT CAC - CHINE AFRIQUE CARGO\n\n";
    csv += "STATISTIQUES\n";
    csv += "Indicateur;Valeur\n";
    csv += `Total colis;${stats.totalParcels}\n`;
    csv += `Livrés;${stats.delivered}\n`;
    csv += `En transit;${stats.inTransit}\n`;
    csv += `Incidents;${stats.incident}\n`;
    csv += `CA Colis;${stats.totalRevenue.toLocaleString()} FCFA\n`;
    csv += `Commandes;${stats.totalOrders}\n`;
    csv += `CA Store;${stats.storeRevenue.toLocaleString()} FCFA\n`;
    csv += `Délai moyen;${stats.avgDeliveryDays} jours\n`;

    downloadFile(csv, filename, "text/csv; charset=utf-8-sig");
    toast.success("✅ Rapport CSV exporté");
  };

  const exportParcelsCSV = () => {
    const filename = `colis_${new Date().toISOString().split("T")[0]}.csv`;

    let csv = "\ufeff";
    csv +=
      "N° Suivi;Destinataire;Expéditeur;Statut;Type;Montant(FCFA);Date création\n";
    parcels.forEach((p) => {
      csv += `${p.tracking_number};${p.recipient_name};${p.sender_name};${getStatusBadge(p.current_status).text};${p.shipment_type};${p.total_cost || 0};${formatShortDate(p.created_at)}\n`;
    });

    downloadFile(csv, filename, "text/csv; charset=utf-8-sig");
    toast.success(`✅ ${parcels.length} colis exportés en CSV`);
  };

  const exportOrdersCSV = () => {
    const filename = `commandes_${new Date().toISOString().split("T")[0]}.csv`;

    let csv = "\ufeff";
    csv +=
      "N° Commande;Client;Email;Téléphone;Montant(FCFA);Statut;Paiement;Date commande\n";
    allOrders.forEach((o) => {
      csv += `${o.order_number};${o.shipping_name};${o.client_email || "-"};${o.shipping_phone || "-"};${o.total_amount || 0};${getOrderStatusBadge(o.status).text};${o.is_paid ? "Payé" : "En attente"};${formatShortDate(o.created_at)}\n`;
    });

    downloadFile(csv, filename, "text/csv; charset=utf-8-sig");
    toast.success(`✅ ${allOrders.length} commandes exportées en CSV`);
  };

  const downloadFile = (content, filename, mimeType) => {
    const blob = new Blob(["\ufeff" + content], { type: mimeType });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", filename);
    document.body.appendChild(link);
    link.click();
    setTimeout(() => {
      link.remove();
      window.URL.revokeObjectURL(url);
    }, 100);
  };

  // ========== VALIDATION FORMULAIRES ==========
  const validateUserForm = () => {
    if (!userForm.email.includes("@")) {
      toast.error("Email invalide");
      return false;
    }
    if (userForm.phone && !userForm.phone.match(/^[\+]?[0-9]{8,15}$/)) {
      toast.error("Téléphone invalide (8-15 chiffres)");
      return false;
    }
    if (!selectedItem && !userForm.password) {
      toast.error("Mot de passe requis pour un nouvel utilisateur");
      return false;
    }
    return true;
  };

  const validateWhatsappForm = () => {
    if (!whatsappForm.phone_number) {
      toast.error("Numéro WhatsApp requis");
      return false;
    }
    if (!whatsappForm.phone_number.match(/^[\+]?[0-9]{8,15}$/)) {
      toast.error("Numéro invalide (8-15 chiffres, + optionnel)");
      return false;
    }
    return true;
  };

  // ========== GESTION UTILISATEURS ==========
  const handleUserSubmit = async (e) => {
    e.preventDefault();
    if (!validateUserForm()) return;

    setUserLoading(true);
    try {
      if (selectedItem) {
        await axios.put(`/auth/users/${selectedItem.id}/`, userForm);
        toast.success("Utilisateur modifié");
      } else {
        await axios.post("/auth/users/", userForm);
        toast.success("Utilisateur créé");
      }
      setShowUserModal(false);
      setSelectedItem(null);
      setUserForm({
        email: "",
        first_name: "",
        last_name: "",
        phone: "",
        role: "operator",
        password: "",
      });
      fetchUsers();
    } catch (err) {
      toast.error(
        err.response?.data?.email?.[0] ||
          err.response?.data?.detail ||
          "Erreur lors de l'enregistrement",
      );
    } finally {
      setUserLoading(false);
    }
  };

  const deleteUser = async (user) => {
    if (
      window.confirm(`Supprimer ${user.email} ? Cette action est irréversible.`)
    ) {
      try {
        await axios.delete(`/auth/users/${user.id}/`);
        toast.success("Utilisateur supprimé");
        fetchUsers();
      } catch (err) {
        toast.error("Erreur lors de la suppression");
      }
    }
  };

  // ========== GESTION WHATSAPP ==========
  const handleWhatsappSubmit = async (e) => {
    e.preventDefault();
    if (!validateWhatsappForm()) return;

    try {
      if (selectedItem) {
        await axios.put(`/whatsapp/config/${selectedItem.id}/`, whatsappForm);
        toast.success("Configuration mise à jour");
      } else {
        await axios.post("/whatsapp/config/", whatsappForm);
        toast.success("Configuration ajoutée");
      }
      setShowWhatsappModal(false);
      setSelectedItem(null);
      setWhatsappForm({
        phone_number: "",
        label: "",
        is_default: false,
        is_active: true,
      });
      fetchWhatsappConfigs();
    } catch (err) {
      toast.error(
        err.response?.data?.detail || "Erreur lors de l'enregistrement",
      );
    }
  };

  const testWhatsapp = async (config) => {
    toast.loading("Envoi du message test...");
    try {
      await axios.post(`/whatsapp/config/${config.id}/test/`);
      toast.dismiss();
      toast.success("✅ Message test envoyé avec succès");
    } catch (err) {
      toast.dismiss();
      toast.error("❌ Échec de l'envoi du message test");
    }
  };

  const deleteWhatsapp = async (config) => {
    if (window.confirm(`Supprimer la configuration ${config.phone_number} ?`)) {
      try {
        await axios.delete(`/whatsapp/config/${config.id}/`);
        toast.success("Configuration supprimée");
        fetchWhatsappConfigs();
      } catch (err) {
        toast.error("Erreur lors de la suppression");
      }
    }
  };

  // ========== RENDU ==========
  if (loading) {
    return (
      <Container
        className="d-flex justify-content-center align-items-center"
        style={{ minHeight: "80vh" }}
      >
        <div className="text-center">
          <Spinner
            animation="border"
            style={{
              color: CHART_COLORS.primary,
              width: "3rem",
              height: "3rem",
            }}
          />
          <p className="mt-3 text-muted">Chargement du dashboard...</p>
        </div>
      </Container>
    );
  }

  return (
    <div className={`admin-dashboard ${darkMode ? "dark-theme" : ""}`}>
      <Container fluid className="px-4 py-4">
        {/* En-tête */}
        <div className="dashboard-header mb-4">
          <div className="d-flex justify-content-between align-items-center flex-wrap">
            <div>
              <h1
                className="fw-bold mb-1"
                style={{ color: CHART_COLORS.primary }}
              >
                <FiBarChart2 className="me-2" />
                Dashboard Administrateur
              </h1>
              <p className="text-muted mb-0">
                Vue d'ensemble de votre activité logistique
                <small className="ms-2 text-muted">
                  Dernière mise à jour: {lastUpdate.toLocaleTimeString("fr-FR")}
                </small>
              </p>
            </div>
            <div className="d-flex gap-2 mt-2 mt-sm-0">
              <OverlayTrigger
                placement="bottom"
                overlay={<Tooltip>Mode sombre</Tooltip>}
              >
                <Button
                  variant="outline-secondary"
                  onClick={() => setDarkMode(!darkMode)}
                >
                  {darkMode ? <FiSun /> : <FiMoon />}
                </Button>
              </OverlayTrigger>
              <OverlayTrigger
                placement="bottom"
                overlay={<Tooltip>Actualiser</Tooltip>}
              >
                <Button
                  variant="outline-secondary"
                  onClick={handleRefresh}
                  disabled={refreshing}
                >
                  <FiRefreshCw className={refreshing ? "spin" : ""} />
                </Button>
              </OverlayTrigger>
              <Dropdown>
                <Dropdown.Toggle variant="danger">
                  <FiDownload className="me-1" /> Exporter
                </Dropdown.Toggle>
                <Dropdown.Menu>
                  <Dropdown.Header>Colis</Dropdown.Header>
                  <Dropdown.Item
                    onClick={() => handleExport("parcels", "xlsx")}
                  >
                    Excel (.xlsx)
                  </Dropdown.Item>
                  <Dropdown.Item onClick={() => handleExport("parcels", "csv")}>
                    CSV (.csv)
                  </Dropdown.Item>
                  <Dropdown.Divider />
                  <Dropdown.Header>Commandes</Dropdown.Header>
                  <Dropdown.Item onClick={() => handleExport("orders", "xlsx")}>
                    Excel (.xlsx)
                  </Dropdown.Item>
                  <Dropdown.Item onClick={() => handleExport("orders", "csv")}>
                    CSV (.csv)
                  </Dropdown.Item>
                  <Dropdown.Divider />
                  <Dropdown.Header>Notifications</Dropdown.Header>
                  <Dropdown.Item
                    onClick={() => handleExport("notifications", "csv")}
                  >
                    Notifications CSV
                  </Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown>
            </div>
          </div>
        </div>

        {error && (
          <Alert variant="danger" className="rounded-3">
            {error}
          </Alert>
        )}

        {/* Tabs principaux */}
        <Tabs
          activeKey={activeTab}
          onSelect={setActiveTab}
          className="custom-tabs mb-4"
          fill
        >
          {/* Tab Vue d'ensemble */}
          <Tab
            eventKey="overview"
            title={
              <>
                <FiPieChart className="me-1" /> Vue d'ensemble
              </>
            }
          >
            <Row className="g-4 mb-4">
              <Col lg={3} md={6}>
                <StatCard
                  icon={FiPackage}
                  value={stats.totalParcels}
                  label="Total colis"
                  color={CHART_COLORS.primary}
                />
              </Col>
              <Col lg={3} md={6}>
                <StatCard
                  icon={FiTruck}
                  value={stats.inTransit}
                  label="En transit"
                  color={CHART_COLORS.info}
                />
              </Col>
              <Col lg={3} md={6}>
                <StatCard
                  icon={FiCheckCircle}
                  value={stats.delivered}
                  label="Livrés"
                  color={CHART_COLORS.success}
                />
              </Col>
              <Col lg={3} md={6}>
                <StatCard
                  icon={FiDollarSign}
                  value={stats.totalRevenue}
                  label="CA Colis"
                  color={CHART_COLORS.warning}
                  suffix=" FCFA"
                />
              </Col>
            </Row>

            <Row className="g-4 mb-4">
              <Col lg={3} md={6}>
                <StatCard
                  icon={FiShoppingBag}
                  value={stats.totalOrders}
                  label="Commandes"
                  color={CHART_COLORS.purple}
                />
              </Col>
              <Col lg={3} md={6}>
                <StatCard
                  icon={FiCheckCircle}
                  value={stats.validatedOrders}
                  label="Validées"
                  color={CHART_COLORS.success}
                />
              </Col>
              <Col lg={3} md={6}>
                <StatCard
                  icon={FiUsers}
                  value={stats.operators}
                  label="Opérateurs"
                  color={CHART_COLORS.info}
                />
              </Col>
              <Col lg={3} md={6}>
                <StatCard
                  icon={FiClock}
                  value={stats.avgDeliveryDays}
                  label="Délai moyen"
                  color={CHART_COLORS.pink}
                  suffix=" jours"
                />
              </Col>
            </Row>

            <Row className="g-4 mb-4">
              <Col lg={6}>
                <Card className="border-0 shadow-sm rounded-4 h-100">
                  <Card.Header className="bg-white border-0 pt-3 pb-0 d-flex justify-content-between align-items-center">
                    <h5 className="fw-bold">📊 Évolution des colis</h5>
                    <small className="text-muted">6 derniers mois</small>
                  </Card.Header>
                  <Card.Body>
                    <div style={{ height: "300px" }}>
                      <Bar data={barChartConfig} options={chartOptions} />
                    </div>
                  </Card.Body>
                </Card>
              </Col>
              <Col lg={6}>
                <Card className="border-0 shadow-sm rounded-4 h-100">
                  <Card.Header className="bg-white border-0 pt-3 pb-0 d-flex justify-content-between align-items-center">
                    <h5 className="fw-bold">📈 Évolution du CA</h5>
                    <small className="text-muted">FCFA</small>
                  </Card.Header>
                  <Card.Body>
                    <div style={{ height: "300px" }}>
                      <Line data={lineChartConfig} options={chartOptions} />
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            </Row>

            <Row className="g-4 mb-4">
              <Col lg={4}>
                <Card className="border-0 shadow-sm rounded-4 h-100">
                  <Card.Header className="bg-white border-0 pt-3 pb-0">
                    <h5 className="fw-bold">🥧 Répartition par statut</h5>
                  </Card.Header>
                  <Card.Body className="d-flex align-items-center">
                    <div style={{ height: "250px", width: "100%" }}>
                      <Pie
                        data={pieChartConfig}
                        options={{
                          ...chartOptions,
                          plugins: {
                            legend: { display: true, position: "bottom" },
                          },
                        }}
                      />
                    </div>
                  </Card.Body>
                </Card>
              </Col>
              <Col lg={4}>
                <Card className="border-0 shadow-sm rounded-4 h-100">
                  <Card.Header className="bg-white border-0 pt-3 pb-0">
                    <h5 className="fw-bold">🚢 Répartition par type</h5>
                  </Card.Header>
                  <Card.Body className="d-flex align-items-center">
                    <div style={{ height: "250px", width: "100%" }}>
                      <Doughnut
                        data={doughnutChartConfig}
                        options={{
                          ...chartOptions,
                          plugins: {
                            legend: { display: true, position: "bottom" },
                          },
                        }}
                      />
                    </div>
                  </Card.Body>
                </Card>
              </Col>
              <Col lg={4}>
                <Card className="border-0 shadow-sm rounded-4 h-100">
                  <Card.Header className="bg-white border-0 pt-3 pb-0">
                    <h5 className="fw-bold">👥 Nouveaux clients</h5>
                  </Card.Header>
                  <Card.Body className="d-flex align-items-center">
                    <div style={{ height: "250px", width: "100%" }}>
                      <Line
                        data={usersChartConfig}
                        options={{
                          ...chartOptions,
                          plugins: {
                            legend: { display: true, position: "bottom" },
                          },
                        }}
                      />
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            </Row>

            <Row className="g-4">
              <Col lg={6}>
                <Card className="border-0 shadow-sm rounded-4">
                  <Card.Header className="bg-white border-0 pt-3 d-flex justify-content-between align-items-center">
                    <h5 className="fw-bold mb-0">📦 Derniers colis</h5>
                    <Link
                      to="#"
                      onClick={() => setActiveTab("parcels")}
                      className="text-danger small"
                    >
                      Voir tout →
                    </Link>
                  </Card.Header>
                  <Card.Body className="p-0">
                    <div className="table-responsive">
                      <Table hover className="mb-0">
                        <thead className="bg-light">
                          <tr>
                            <th>N° Suivi</th>
                            <th>Destinataire</th>
                            <th>Statut</th>
                            <th>Montant</th>
                          </tr>
                        </thead>
                        <tbody>
                          {parcels.slice(0, 5).map((p) => {
                            const status = getStatusBadge(p.current_status);
                            return (
                              <tr key={p.id}>
                                <td className="font-monospace">
                                  {p.tracking_number}
                                </td>
                                <td>{p.recipient_name}</td>
                                <td>
                                  <Badge bg={status.variant}>
                                    {status.text}
                                  </Badge>
                                </td>
                                <td>{formatPrice(p.total_cost)} FCFA</td>
                              </tr>
                            );
                          })}
                          {parcels.length === 0 && (
                            <tr>
                              <td
                                colSpan="4"
                                className="text-center text-muted py-4"
                              >
                                Aucun colis
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </Table>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
              <Col lg={6}>
                <Card className="border-0 shadow-sm rounded-4">
                  <Card.Header className="bg-white border-0 pt-3 d-flex justify-content-between align-items-center">
                    <h5 className="fw-bold mb-0">🛍️ Dernières commandes</h5>
                    <Link
                      to="#"
                      onClick={() => setActiveTab("orders")}
                      className="text-danger small"
                    >
                      Voir tout →
                    </Link>
                  </Card.Header>
                  <Card.Body className="p-0">
                    <div className="table-responsive">
                      <Table hover className="mb-0">
                        <thead className="bg-light">
                          <tr>
                            <th>N° Commande</th>
                            <th>Client</th>
                            <th>Statut</th>
                            <th>Montant</th>
                          </tr>
                        </thead>
                        <tbody>
                          {allOrders.slice(0, 5).map((o) => {
                            const status = getOrderStatusBadge(o.status);
                            return (
                              <tr key={o.id}>
                                <td className="font-monospace">
                                  {o.order_number}
                                </td>
                                <td>{o.client_email}</td>
                                <td>
                                  <Badge bg={status.variant}>
                                    {status.text}
                                  </Badge>
                                </td>
                                <td>{formatPrice(o.total_amount)} FCFA</td>
                              </tr>
                            );
                          })}
                          {allOrders.length === 0 && (
                            <tr>
                              <td
                                colSpan="4"
                                className="text-center text-muted py-4"
                              >
                                Aucune commande
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </Table>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          </Tab>

          {/* Tab Colis */}
          <Tab
            eventKey="parcels"
            title={
              <>
                <FiPackage className="me-1" /> Colis ({filteredParcels.length})
              </>
            }
          >
            <Card className="border-0 shadow-sm rounded-4">
              <Card.Header className="bg-white border-0 pt-4">
                <div className="d-flex justify-content-between align-items-center flex-wrap gap-3">
                  <h4 className="fw-bold mb-0">
                    📦 Gestion des colis ({filteredParcels.length})
                  </h4>
                  <div className="d-flex gap-2">
                    <InputGroup style={{ width: "280px" }}>
                      <InputGroup.Text>
                        <FiSearch />
                      </InputGroup.Text>
                      <Form.Control
                        placeholder="Rechercher..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                      />
                      {searchTerm && (
                        <Button
                          variant="outline-secondary"
                          onClick={() => setSearchTerm("")}
                        >
                          <FiX />
                        </Button>
                      )}
                    </InputGroup>
                    <Dropdown>
                      <Dropdown.Toggle variant="outline-secondary">
                        <FiFilter className="me-1" />
                        {statusFilter === "all"
                          ? "Tous"
                          : statusFilter === "transit"
                            ? "En transit"
                            : getStatusBadge(statusFilter).text}
                      </Dropdown.Toggle>
                      <Dropdown.Menu>
                        <Dropdown.Item onClick={() => setStatusFilter("all")}>
                          Tous
                        </Dropdown.Item>
                        <Dropdown.Item
                          onClick={() => setStatusFilter("transit")}
                        >
                          En transit
                        </Dropdown.Item>
                        <Dropdown.Item
                          onClick={() => setStatusFilter("delivered")}
                        >
                          Livrés
                        </Dropdown.Item>
                        <Dropdown.Item
                          onClick={() => setStatusFilter("incident")}
                        >
                          Incidents
                        </Dropdown.Item>
                      </Dropdown.Menu>
                    </Dropdown>
                  </div>
                </div>
              </Card.Header>
              <Card.Body className="p-0">
                <div className="table-responsive">
                  <Table hover className="mb-0">
                    <thead className="bg-light">
                      <tr>
                        <th>N° Suivi</th>
                        <th>Destinataire</th>
                        <th>Expéditeur</th>
                        <th>Type</th>
                        <th>Statut</th>
                        <th>Montant</th>
                        <th>Date</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paginatedParcels.map((p) => {
                        const status = getStatusBadge(p.current_status);
                        return (
                          <tr key={p.id}>
                            <td className="font-monospace">
                              {p.tracking_number}
                            </td>
                            <td>{p.recipient_name}</td>
                            <td>{p.sender_name}</td>
                            <td>
                              {p.shipment_type === "air"
                                ? "✈️ Aérien"
                                : p.shipment_type === "sea"
                                  ? "🚢 Maritime"
                                  : "-"}
                            </td>
                            <td>
                              <Badge bg={status.variant}>{status.text}</Badge>
                            </td>
                            <td>{formatPrice(p.total_cost)} FCFA</td>
                            <td>{formatShortDate(p.created_at)}</td>
                            <td>
                              <div className="d-flex gap-1">
                                <Link
                                  to={`/tracking/${p.tracking_number}`}
                                  target="_blank"
                                  className="btn btn-sm btn-outline-primary"
                                >
                                  <FiEye />
                                </Link>
                                <Button
                                  variant="outline-danger"
                                  size="sm"
                                  onClick={() => {
                                    if (
                                      window.confirm(
                                        `Supprimer le colis ${p.tracking_number} ?`,
                                      )
                                    ) {
                                      axios
                                        .delete(`/parcels/${p.id}/`)
                                        .then(() => {
                                          toast.success("Colis supprimé");
                                          fetchParcels();
                                        })
                                        .catch(() =>
                                          toast.error("Erreur suppression"),
                                        );
                                    }
                                  }}
                                >
                                  <FiTrash2 />
                                </Button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                      {paginatedParcels.length === 0 && (
                        <tr>
                          <td
                            colSpan="8"
                            className="text-center text-muted py-4"
                          >
                            Aucun colis trouvé
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </Table>
                </div>

                {totalParcelPages > 1 && (
                  <div className="d-flex justify-content-center py-3">
                    <Pagination>
                      <Pagination.First
                        onClick={() => setParcelsPage(1)}
                        disabled={parcelsPage === 1}
                      />
                      <Pagination.Prev
                        onClick={() =>
                          setParcelsPage((p) => Math.max(1, p - 1))
                        }
                        disabled={parcelsPage === 1}
                      />
                      <Pagination.Item active>
                        {parcelsPage} / {totalParcelPages}
                      </Pagination.Item>
                      <Pagination.Next
                        onClick={() =>
                          setParcelsPage((p) =>
                            Math.min(totalParcelPages, p + 1),
                          )
                        }
                        disabled={parcelsPage === totalParcelPages}
                      />
                      <Pagination.Last
                        onClick={() => setParcelsPage(totalParcelPages)}
                        disabled={parcelsPage === totalParcelPages}
                      />
                    </Pagination>
                  </div>
                )}
              </Card.Body>
            </Card>
          </Tab>

          {/* Tab Commandes */}
          <Tab
            eventKey="orders"
            title={
              <>
                <FiShoppingBag className="me-1" /> Commandes (
                {filteredOrders.length})
              </>
            }
          >
            <Row className="mb-4">
              <Col md={4}>
                <Card className="border-0 shadow-sm bg-success bg-opacity-10">
                  <Card.Body className="text-center">
                    <h3 className="fw-bold text-success">
                      {validatedOrders.length}
                    </h3>
                    <p className="text-muted mb-0">Commandes validées</p>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={4}>
                <Card className="border-0 shadow-sm bg-warning bg-opacity-10">
                  <Card.Body className="text-center">
                    <h3 className="fw-bold text-warning">
                      {allOrders.filter((o) => o.status === "pending").length}
                    </h3>
                    <p className="text-muted mb-0">En attente</p>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={4}>
                <Card className="border-0 shadow-sm bg-info bg-opacity-10">
                  <Card.Body className="text-center">
                    <h3 className="fw-bold text-info">
                      {allOrders.filter((o) => o.status === "shipped").length}
                    </h3>
                    <p className="text-muted mb-0">Expédiées</p>
                  </Card.Body>
                </Card>
              </Col>
            </Row>

            <Card className="border-0 shadow-sm rounded-4">
              <Card.Header className="bg-white border-0 pt-4">
                <h4 className="fw-bold mb-0">
                  🛍️ Toutes les commandes ({filteredOrders.length})
                </h4>
              </Card.Header>
              <Card.Body className="p-0">
                <div className="table-responsive">
                  <Table hover className="mb-0">
                    <thead className="bg-light">
                      <tr>
                        <th>N° Commande</th>
                        <th>Client</th>
                        <th>Montant</th>
                        <th>Statut</th>
                        <th>Date</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paginatedOrders.map((o) => {
                        const status = getOrderStatusBadge(o.status);
                        return (
                          <tr key={o.id}>
                            <td className="font-monospace">{o.order_number}</td>
                            <td>
                              {o.shipping_name}
                              <br />
                              <small className="text-muted">
                                {o.client_email}
                              </small>
                            </td>
                            <td>{formatPrice(o.total_amount)} FCFA</td>
                            <td>
                              <Badge bg={status.variant}>{status.text}</Badge>
                            </td>
                            <td>{formatShortDate(o.created_at)}</td>
                            <td>
                              <Button
                                variant="outline-primary"
                                size="sm"
                                onClick={() => {
                                  setSelectedItem(o);
                                  setShowOrderModal(true);
                                }}
                              >
                                <FiEye /> Détails
                              </Button>
                            </td>
                          </tr>
                        );
                      })}
                      {paginatedOrders.length === 0 && (
                        <tr>
                          <td
                            colSpan="6"
                            className="text-center text-muted py-4"
                          >
                            Aucune commande
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </Table>
                </div>

                {totalOrderPages > 1 && (
                  <div className="d-flex justify-content-center py-3">
                    <Pagination>
                      <Pagination.First
                        onClick={() => setOrdersPage(1)}
                        disabled={ordersPage === 1}
                      />
                      <Pagination.Prev
                        onClick={() => setOrdersPage((p) => Math.max(1, p - 1))}
                        disabled={ordersPage === 1}
                      />
                      <Pagination.Item active>
                        {ordersPage} / {totalOrderPages}
                      </Pagination.Item>
                      <Pagination.Next
                        onClick={() =>
                          setOrdersPage((p) => Math.min(totalOrderPages, p + 1))
                        }
                        disabled={ordersPage === totalOrderPages}
                      />
                      <Pagination.Last
                        onClick={() => setOrdersPage(totalOrderPages)}
                        disabled={ordersPage === totalOrderPages}
                      />
                    </Pagination>
                  </div>
                )}
              </Card.Body>
            </Card>
          </Tab>

          {/* Tab Utilisateurs */}
          <Tab
            eventKey="users"
            title={
              <>
                <FiUsers className="me-1" /> Utilisateurs
              </>
            }
          >
            <div className="d-flex justify-content-end mb-3">
              <Button
                variant="danger"
                onClick={() => {
                  setSelectedItem(null);
                  setUserForm({
                    email: "",
                    first_name: "",
                    last_name: "",
                    phone: "",
                    role: "operator",
                    password: "",
                  });
                  setShowUserModal(true);
                }}
              >
                <FiUserPlus className="me-1" /> Nouvel utilisateur
              </Button>
            </div>

            <Tabs defaultActiveKey="operators" className="mb-3">
              <Tab
                eventKey="operators"
                title={`👥 Opérateurs (${users.filter((u) => u.role === "operator").length})`}
              >
                <Card className="border-0 shadow-sm">
                  <Card.Body className="p-0">
                    <div className="table-responsive">
                      <Table hover className="mb-0">
                        <thead className="bg-light">
                          <tr>
                            <th>Nom</th>
                            <th>Email</th>
                            <th>Téléphone</th>
                            <th>Date</th>
                            <th>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {users
                            .filter((u) => u.role === "operator")
                            .map((u) => (
                              <tr key={u.id}>
                                <td>
                                  {u.first_name} {u.last_name}
                                </td>
                                <td>{u.email}</td>
                                <td>{u.phone || "-"}</td>
                                <td>{formatShortDate(u.date_joined)}</td>
                                <td>
                                  <Button
                                    variant="outline-primary"
                                    size="sm"
                                    className="me-1"
                                    onClick={() => {
                                      setSelectedItem(u);
                                      setUserForm({
                                        email: u.email,
                                        first_name: u.first_name || "",
                                        last_name: u.last_name || "",
                                        phone: u.phone || "",
                                        role: u.role,
                                        password: "",
                                      });
                                      setShowUserModal(true);
                                    }}
                                  >
                                    <FiEdit />
                                  </Button>
                                  <Button
                                    variant="outline-danger"
                                    size="sm"
                                    onClick={() => deleteUser(u)}
                                  >
                                    <FiTrash2 />
                                  </Button>
                                </td>
                              </tr>
                            ))}
                          {users.filter((u) => u.role === "operator").length ===
                            0 && (
                            <tr>
                              <td
                                colSpan="5"
                                className="text-center text-muted py-4"
                              >
                                Aucun opérateur
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </Table>
                    </div>
                  </Card.Body>
                </Card>
              </Tab>
              <Tab
                eventKey="admins"
                title={`👑 Administrateurs (${users.filter((u) => u.role === "admin").length})`}
              >
                <Card className="border-0 shadow-sm">
                  <Card.Body className="p-0">
                    <div className="table-responsive">
                      <Table hover className="mb-0">
                        <thead className="bg-light">
                          <tr>
                            <th>Nom</th>
                            <th>Email</th>
                            <th>Téléphone</th>
                            <th>Date</th>
                            <th>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {users
                            .filter((u) => u.role === "admin")
                            .map((u) => (
                              <tr key={u.id}>
                                <td>
                                  {u.first_name} {u.last_name}
                                </td>
                                <td>{u.email}</td>
                                <td>{u.phone || "-"}</td>
                                <td>{formatShortDate(u.date_joined)}</td>
                                <td>
                                  <Button
                                    variant="outline-primary"
                                    size="sm"
                                    className="me-1"
                                    onClick={() => {
                                      setSelectedItem(u);
                                      setUserForm({
                                        email: u.email,
                                        first_name: u.first_name || "",
                                        last_name: u.last_name || "",
                                        phone: u.phone || "",
                                        role: u.role,
                                        password: "",
                                      });
                                      setShowUserModal(true);
                                    }}
                                  >
                                    <FiEdit />
                                  </Button>
                                  <Button
                                    variant="outline-danger"
                                    size="sm"
                                    onClick={() => deleteUser(u)}
                                  >
                                    <FiTrash2 />
                                  </Button>
                                </td>
                              </tr>
                            ))}
                          {users.filter((u) => u.role === "admin").length ===
                            0 && (
                            <tr>
                              <td
                                colSpan="5"
                                className="text-center text-muted py-4"
                              >
                                Aucun administrateur
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </Table>
                    </div>
                  </Card.Body>
                </Card>
              </Tab>
              <Tab
                eventKey="clients"
                title={`🛒 Clients (${users.filter((u) => u.role === "client").length})`}
              >
                <Card className="border-0 shadow-sm">
                  <Card.Body className="p-0">
                    <div className="table-responsive">
                      <Table hover className="mb-0">
                        <thead className="bg-light">
                          <td>
                            <th>Nom</th>
                            <th>Email</th>
                            <th>Téléphone</th>
                            <th>Date</th>
                          </td>
                        </thead>
                        <tbody>
                          {users
                            .filter((u) => u.role === "client")
                            .map((u) => (
                              <tr key={u.id}>
                                <td>
                                  {u.first_name} {u.last_name}
                                </td>
                                <td>{u.email}</td>
                                <td>{u.phone || "-"}</td>
                                <td>{formatShortDate(u.date_joined)}</td>
                              </tr>
                            ))}
                          {users.filter((u) => u.role === "client").length ===
                            0 && (
                            <tr>
                              <td
                                colSpan="4"
                                className="text-center text-muted py-4"
                              >
                                Aucun client
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </Table>
                    </div>
                  </Card.Body>
                </Card>
              </Tab>
            </Tabs>
          </Tab>

          {/* Tab WhatsApp */}
          <Tab
            eventKey="whatsapp"
            title={
              <>
                <FiSend className="me-1" /> WhatsApp
              </>
            }
          >
            <Card className="border-0 shadow-sm rounded-4">
              <Card.Header className="bg-white border-0 pt-4">
                <div className="d-flex justify-content-between align-items-center flex-wrap gap-3">
                  <div>
                    <h4 className="fw-bold mb-0">📱 Configuration WhatsApp</h4>
                    <p className="text-muted mb-0 mt-1">
                      Gérez les numéros WhatsApp pour les notifications
                    </p>
                  </div>
                  <Button
                    variant="danger"
                    onClick={() => {
                      setSelectedItem(null);
                      setWhatsappForm({
                        phone_number: "",
                        label: "",
                        is_default: false,
                        is_active: true,
                      });
                      setShowWhatsappModal(true);
                    }}
                  >
                    <FiPlus className="me-1" /> Ajouter un numéro
                  </Button>
                </div>
              </Card.Header>
              <Card.Body>
                {whatsappConfigs.length === 0 ? (
                  <div className="text-center py-5">
                    <FiSend size={48} className="text-muted mb-3" />
                    <h5>Aucune configuration WhatsApp</h5>
                    <p className="text-muted">
                      Ajoutez votre premier numéro WhatsApp pour envoyer des
                      notifications
                    </p>
                    <Button
                      variant="danger"
                      onClick={() => setShowWhatsappModal(true)}
                    >
                      <FiPlus /> Ajouter un numéro
                    </Button>
                  </div>
                ) : (
                  <div className="table-responsive">
                    <Table hover className="mb-0">
                      <thead className="bg-light">
                        <tr>
                          <th>Numéro</th>
                          <th>Libellé</th>
                          <th>Défaut</th>
                          <th>Statut</th>
                          <th>Dernière utilisation</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {whatsappConfigs.map((config) => (
                          <tr
                            key={config.id}
                            className={config.is_default ? "table-primary" : ""}
                          >
                            <td className="font-monospace">
                              {config.phone_number}
                            </td>
                            <td>{config.label || "-"}</td>
                            <td>
                              {config.is_default ? (
                                <Badge bg="success">✅ Par défaut</Badge>
                              ) : (
                                <Badge bg="secondary">❌</Badge>
                              )}
                            </td>
                            <td>
                              <Badge
                                bg={config.is_active ? "success" : "secondary"}
                              >
                                {config.is_active ? "Actif" : "Inactif"}
                              </Badge>
                            </td>
                            <td>
                              {config.last_used
                                ? formatShortDate(config.last_used)
                                : "Jamais"}
                            </td>
                            <td>
                              <div className="d-flex gap-1">
                                <Button
                                  size="sm"
                                  variant="outline-success"
                                  onClick={() => testWhatsapp(config)}
                                  title="Tester l'envoi"
                                >
                                  <FiSend />
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline-primary"
                                  onClick={() => {
                                    setSelectedItem(config);
                                    setWhatsappForm({
                                      phone_number: config.phone_number,
                                      label: config.label || "",
                                      is_default: config.is_default,
                                      is_active: config.is_active,
                                    });
                                    setShowWhatsappModal(true);
                                  }}
                                >
                                  <FiEdit />
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline-danger"
                                  onClick={() => deleteWhatsapp(config)}
                                >
                                  <FiTrash2 />
                                </Button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                )}
              </Card.Body>
            </Card>
          </Tab>

          {/* Tab Rapports */}
          <Tab
            eventKey="reports"
            title={
              <>
                <FiFileText className="me-1" /> Rapports
              </>
            }
          >
            <Card className="border-0 shadow-sm rounded-4">
              <Card.Body className="p-4">
                <h4
                  className="fw-bold mb-4"
                  style={{ color: CHART_COLORS.primary }}
                >
                  📊 Génération de rapports
                </h4>

                <Row className="mb-4">
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label>Période</Form.Label>
                      <Form.Select
                        value={reportPeriod}
                        onChange={(e) => setReportPeriod(e.target.value)}
                      >
                        <option value="day">Aujourd'hui</option>
                        <option value="week">Cette semaine</option>
                        <option value="month">Ce mois</option>
                        <option value="quarter">Ce trimestre</option>
                        <option value="year">Cette année</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label>Format</Form.Label>
                      <Form.Select
                        value={reportFormat}
                        onChange={(e) => setReportFormat(e.target.value)}
                      >
                        <option value="pdf">PDF</option>
                        <option value="xlsx">Excel</option>
                        <option value="csv">CSV</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  <Col md={4} className="d-flex align-items-end">
                    <Button
                      variant="danger"
                      className="me-2"
                      onClick={() => setShowReportModal(true)}
                    >
                      <FiEye className="me-1" /> Aperçu
                    </Button>
                    <Button
                      variant="success"
                      onClick={() => handleExport("report", reportFormat)}
                      disabled={exportLoading}
                    >
                      <FiDownload className="me-1" /> Exporter
                    </Button>
                  </Col>
                </Row>

                <hr />

                <h5 className="fw-bold mb-3">📈 Résumé de la période</h5>
                <Row>
                  <Col md={6}>
                    <div className="bg-light p-3 rounded-3">
                      <div className="d-flex justify-content-between mb-2">
                        <span>Total colis :</span>
                        <strong>{stats.totalParcels}</strong>
                      </div>
                      <div className="d-flex justify-content-between mb-2">
                        <span>Colis livrés :</span>
                        <strong className="text-success">
                          {stats.delivered}
                        </strong>
                      </div>
                      <div className="d-flex justify-content-between mb-2">
                        <span>En transit :</span>
                        <strong className="text-primary">
                          {stats.inTransit}
                        </strong>
                      </div>
                      <div className="d-flex justify-content-between">
                        <span>Incidents :</span>
                        <strong className="text-danger">
                          {stats.incident}
                        </strong>
                      </div>
                    </div>
                  </Col>
                  <Col md={6}>
                    <div className="bg-light p-3 rounded-3">
                      <div className="d-flex justify-content-between mb-2">
                        <span>CA Colis :</span>
                        <strong className="text-success">
                          {stats.totalRevenue.toLocaleString()} FCFA
                        </strong>
                      </div>
                      <div className="d-flex justify-content-between mb-2">
                        <span>CA Store :</span>
                        <strong className="text-info">
                          {stats.storeRevenue.toLocaleString()} FCFA
                        </strong>
                      </div>
                      <div className="d-flex justify-content-between mb-2">
                        <span>Commandes :</span>
                        <strong>{stats.totalOrders}</strong>
                      </div>
                      <div className="d-flex justify-content-between">
                        <span>Délai moyen :</span>
                        <strong>{stats.avgDeliveryDays} jours</strong>
                      </div>
                    </div>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          </Tab>

          {/* Tab Notifications AVEC SUPPORT WHATSAPP */}
          <Tab
            eventKey="notifications"
            title={
              <>
                <FiBell className="me-1" /> Notifications (
                {filteredNotifications.length})
              </>
            }
          >
            <Card className="border-0 shadow-sm rounded-4">
              <Card.Header className="bg-white border-0 pt-4">
                <div className="d-flex justify-content-between align-items-center flex-wrap gap-3">
                  <div>
                    <h4 className="fw-bold mb-0">
                      📬 Historique des notifications (
                      {filteredNotifications.length})
                    </h4>
                    <p className="text-muted mb-0 mt-1">
                      Emails et notifications WhatsApp envoyés aux utilisateurs
                    </p>
                  </div>
                  <div className="d-flex gap-2">
                    <InputGroup style={{ width: "250px" }}>
                      <InputGroup.Text>
                        <FiSearch />
                      </InputGroup.Text>
                      <Form.Control
                        placeholder="Rechercher..."
                        value={notificationSearch}
                        onChange={(e) => {
                          setNotificationSearch(e.target.value);
                          setNotificationsPage(1);
                        }}
                      />
                      {notificationSearch && (
                        <Button
                          variant="outline-secondary"
                          onClick={() => setNotificationSearch("")}
                        >
                          <FiX />
                        </Button>
                      )}
                    </InputGroup>
                    <Dropdown>
                      <Dropdown.Toggle variant="outline-secondary">
                        <FiFilter className="me-1" />
                        {notificationFilter === "all"
                          ? "Tous"
                          : notificationFilter === "whatsapp_sent"
                            ? "📱 WhatsApp envoyés"
                            : notificationFilter === "whatsapp_failed"
                              ? "❌ WhatsApp échoués"
                              : notificationFilter === "whatsapp_pending"
                                ? "⏳ WhatsApp en attente"
                                : getNotificationTypeBadge(notificationFilter)
                                    .text}
                      </Dropdown.Toggle>
                      <Dropdown.Menu>
                        <Dropdown.Item
                          onClick={() => {
                            setNotificationFilter("all");
                            setNotificationsPage(1);
                          }}
                        >
                          📋 Tous
                        </Dropdown.Item>
                        <Dropdown.Divider />
                        <Dropdown.Item
                          onClick={() => {
                            setNotificationFilter("whatsapp_sent");
                            setNotificationsPage(1);
                          }}
                        >
                          📱 WhatsApp envoyés
                        </Dropdown.Item>
                        <Dropdown.Item
                          onClick={() => {
                            setNotificationFilter("whatsapp_failed");
                            setNotificationsPage(1);
                          }}
                        >
                          ❌ WhatsApp échoués
                        </Dropdown.Item>
                        <Dropdown.Item
                          onClick={() => {
                            setNotificationFilter("whatsapp_pending");
                            setNotificationsPage(1);
                          }}
                        >
                          ⏳ WhatsApp en attente
                        </Dropdown.Item>
                        <Dropdown.Divider />
                        <Dropdown.Item
                          onClick={() => {
                            setNotificationFilter("order");
                            setNotificationsPage(1);
                          }}
                        >
                          🛍️ Commandes
                        </Dropdown.Item>
                        <Dropdown.Item
                          onClick={() => {
                            setNotificationFilter("parcel");
                            setNotificationsPage(1);
                          }}
                        >
                          📦 Colis
                        </Dropdown.Item>
                        <Dropdown.Item
                          onClick={() => {
                            setNotificationFilter("payment");
                            setNotificationsPage(1);
                          }}
                        >
                          💳 Paiements
                        </Dropdown.Item>
                        <Dropdown.Item
                          onClick={() => {
                            setNotificationFilter("system");
                            setNotificationsPage(1);
                          }}
                        >
                          ⚙️ Système
                        </Dropdown.Item>
                        <Dropdown.Item
                          onClick={() => {
                            setNotificationFilter("alert");
                            setNotificationsPage(1);
                          }}
                        >
                          🚨 Alertes
                        </Dropdown.Item>
                        <Dropdown.Item
                          onClick={() => {
                            setNotificationFilter("promo");
                            setNotificationsPage(1);
                          }}
                        >
                          🎉 Promotions
                        </Dropdown.Item>
                      </Dropdown.Menu>
                    </Dropdown>
                    <Button
                      variant="outline-secondary"
                      onClick={() => {
                        fetchNotifications();
                        setNotificationsPage(1);
                      }}
                      disabled={notificationsLoading}
                    >
                      <FiRefreshCw
                        className={notificationsLoading ? "spin" : ""}
                      />
                    </Button>
                    <Button
                      variant="outline-success"
                      onClick={() => handleExport("notifications", "csv")}
                    >
                      <FiDownload /> CSV
                    </Button>
                  </div>
                </div>
              </Card.Header>

              {/* Stats rapides avec WhatsApp */}
              <Card.Body className="pb-0">
                <Row className="g-3 mb-3">
                  <Col md={3} xs={6}>
                    <div className="bg-light rounded-3 p-3 text-center">
                      <div className="fw-bold text-primary">
                        {notifications.filter((n) => !n.is_read).length}
                      </div>
                      <small className="text-muted">Non lues</small>
                    </div>
                  </Col>
                  <Col md={3} xs={6}>
                    <div className="bg-light rounded-3 p-3 text-center">
                      <div className="fw-bold text-success">
                        {
                          notifications.filter((n) => n.email_sent === true)
                            .length
                        }
                      </div>
                      <small className="text-muted">📧 Emails envoyés</small>
                    </div>
                  </Col>
                  <Col md={3} xs={6}>
                    <div className="bg-light rounded-3 p-3 text-center">
                      <div className="fw-bold text-success">
                        {
                          notifications.filter((n) => n.whatsapp_sent === true)
                            .length
                        }
                      </div>
                      <small className="text-muted">📱 WhatsApp envoyés</small>
                    </div>
                  </Col>
                  <Col md={3} xs={6}>
                    <div className="bg-light rounded-3 p-3 text-center">
                      <div className="fw-bold text-danger">
                        {
                          notifications.filter((n) => n.priority === "urgent")
                            .length
                        }
                      </div>
                      <small className="text-muted">Urgentes</small>
                    </div>
                  </Col>
                </Row>
              </Card.Body>

              <Card.Body className="p-0">
                {notificationsLoading ? (
                  <div className="text-center py-5">
                    <Spinner
                      animation="border"
                      style={{ color: CHART_COLORS.primary }}
                    />
                    <p className="mt-2 text-muted">
                      Chargement des notifications...
                    </p>
                  </div>
                ) : (
                  <div className="table-responsive">
                    <Table hover className="mb-0">
                      <thead className="bg-light">
                        <tr>
                          <th style={{ width: "50px" }}></th>
                          <th>Type</th>
                          <th>Titre / Message</th>
                          <th>Destinataire</th>
                          <th>Priorité</th>
                          <th>Canaux</th>
                          <th>WhatsApp</th>
                          <th>Email</th>
                          <th>Date</th>
                          <th>Statut</th>
                        </tr>
                      </thead>
                      <tbody>
                        {paginatedNotifications.map((n) => {
                          const typeConfig = getNotificationTypeBadge(
                            n.notification_type,
                          );
                          const priorityConfig = getPriorityBadge(n.priority);
                          return (
                            <tr
                              key={n.id}
                              className={!n.is_read ? "fw-bold bg-light" : ""}
                            >
                              <td className="text-center">
                                {n.notification_type === "order"
                                  ? "🛍️"
                                  : n.notification_type === "parcel"
                                    ? "📦"
                                    : n.notification_type === "payment"
                                      ? "💳"
                                      : n.notification_type === "alert"
                                        ? "🚨"
                                        : n.notification_type === "system"
                                          ? "⚙️"
                                          : n.notification_type === "promo"
                                            ? "🎉"
                                            : "💬"}
                              </td>
                              <td>
                                <Badge bg={typeConfig.variant}>
                                  {typeConfig.text}
                                </Badge>
                              </td>
                              <td>
                                <div>{n.title}</div>
                                <small className="text-muted">
                                  {n.message?.substring(0, 80)}
                                  {n.message?.length > 80 ? "..." : ""}
                                </small>
                              </td>
                              <td>
                                <div>
                                  {n.recipient_name || n.recipient_email || "-"}
                                </div>
                                <small className="text-muted">
                                  {n.recipient_email || ""}
                                </small>
                              </td>
                              <td>
                                <Badge bg={priorityConfig.variant}>
                                  {priorityConfig.text}
                                </Badge>
                              </td>
                              <td>
                                <div className="d-flex gap-1">
                                  {n.channels?.includes("whatsapp") && (
                                    <Badge bg="success" className="p-1">
                                      📱
                                    </Badge>
                                  )}
                                  {n.channels?.includes("email") && (
                                    <Badge bg="info" className="p-1">
                                      📧
                                    </Badge>
                                  )}
                                  {n.channels?.includes("in_app") && (
                                    <Badge bg="secondary" className="p-1">
                                      📱
                                    </Badge>
                                  )}
                                </div>
                              </td>
                              <td>
                                {n.whatsapp_sent === true ? (
                                  <Badge bg="success">✅ Envoyé</Badge>
                                ) : n.whatsapp_error ? (
                                  <OverlayTrigger
                                    overlay={
                                      <Tooltip>{n.whatsapp_error}</Tooltip>
                                    }
                                  >
                                    <Badge bg="danger">❌ Échec</Badge>
                                  </OverlayTrigger>
                                ) : n.channels?.includes("whatsapp") ? (
                                  <Badge bg="warning">⏳ En attente</Badge>
                                ) : (
                                  <Badge bg="secondary">⚪ Non requis</Badge>
                                )}
                                {n.whatsapp_sent_at && (
                                  <div>
                                    <small className="text-muted">
                                      {formatShortDate(n.whatsapp_sent_at)}
                                    </small>
                                  </div>
                                )}
                              </td>
                              <td>
                                {n.email_sent === true ? (
                                  <Badge bg="success">✅ Envoyé</Badge>
                                ) : n.email_error ? (
                                  <OverlayTrigger
                                    overlay={<Tooltip>{n.email_error}</Tooltip>}
                                  >
                                    <Badge bg="danger">❌ Échec</Badge>
                                  </OverlayTrigger>
                                ) : n.channels?.includes("email") ? (
                                  <Badge bg="warning">⏳ En attente</Badge>
                                ) : (
                                  <Badge bg="secondary">⚪ Non requis</Badge>
                                )}
                              </td>
                              <td>
                                <div>{formatShortDate(n.created_at)}</div>
                                <small className="text-muted">
                                  {new Date(n.created_at).toLocaleTimeString(
                                    "fr-FR",
                                    { hour: "2-digit", minute: "2-digit" },
                                  )}
                                </small>
                              </td>
                              <td>
                                {n.is_read ? (
                                  <Badge bg="secondary">✅ Lu</Badge>
                                ) : (
                                  <Badge bg="warning">📬 Non lu</Badge>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                        {paginatedNotifications.length === 0 && (
                          <tr>
                            <td
                              colSpan="10"
                              className="text-center text-muted py-5"
                            >
                              <FiBell size={40} className="mb-3 opacity-50" />
                              <p>Aucune notification trouvée</p>
                              <small>
                                Les emails et notifications WhatsApp
                                apparaîtront ici
                              </small>
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </Table>
                  </div>
                )}

                {totalNotificationPages > 1 && (
                  <div className="d-flex justify-content-center py-3">
                    <Pagination>
                      <Pagination.First
                        onClick={() => setNotificationsPage(1)}
                        disabled={notificationsPage === 1}
                      />
                      <Pagination.Prev
                        onClick={() =>
                          setNotificationsPage((p) => Math.max(1, p - 1))
                        }
                        disabled={notificationsPage === 1}
                      />
                      <Pagination.Item active>
                        {notificationsPage} / {totalNotificationPages}
                      </Pagination.Item>
                      <Pagination.Next
                        onClick={() =>
                          setNotificationsPage((p) =>
                            Math.min(totalNotificationPages, p + 1),
                          )
                        }
                        disabled={notificationsPage === totalNotificationPages}
                      />
                      <Pagination.Last
                        onClick={() =>
                          setNotificationsPage(totalNotificationPages)
                        }
                        disabled={notificationsPage === totalNotificationPages}
                      />
                    </Pagination>
                  </div>
                )}
              </Card.Body>
            </Card>
          </Tab>
        </Tabs>
      </Container>

      {/* Modals */}
      <Modal
        show={showUserModal}
        onHide={() => setShowUserModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {selectedItem ? "✏️ Modifier" : "➕ Créer"} un utilisateur
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleUserSubmit}>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>
                Email <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                type="email"
                value={userForm.email}
                onChange={(e) =>
                  setUserForm({ ...userForm, email: e.target.value })
                }
                required
              />
            </Form.Group>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Prénom</Form.Label>
                  <Form.Control
                    value={userForm.first_name}
                    onChange={(e) =>
                      setUserForm({ ...userForm, first_name: e.target.value })
                    }
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Nom</Form.Label>
                  <Form.Control
                    value={userForm.last_name}
                    onChange={(e) =>
                      setUserForm({ ...userForm, last_name: e.target.value })
                    }
                  />
                </Form.Group>
              </Col>
            </Row>
            <Form.Group className="mb-3">
              <Form.Label>Téléphone</Form.Label>
              <Form.Control
                value={userForm.phone}
                onChange={(e) =>
                  setUserForm({ ...userForm, phone: e.target.value })
                }
                placeholder="+237XXXXXXXXX"
              />
              <Form.Text className="text-muted">
                Format: +237XXXXXXXXX ou 6XXXXXXXX
              </Form.Text>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Rôle</Form.Label>
              <Form.Select
                value={userForm.role}
                onChange={(e) =>
                  setUserForm({ ...userForm, role: e.target.value })
                }
              >
                <option value="operator">Opérateur</option>
                <option value="admin">Administrateur</option>
              </Form.Select>
            </Form.Group>
            {!selectedItem && (
              <Form.Group className="mb-3">
                <Form.Label>Mot de passe</Form.Label>
                <Form.Control
                  type="text"
                  value={userForm.password}
                  onChange={(e) =>
                    setUserForm({ ...userForm, password: e.target.value })
                  }
                  placeholder="Laissé vide = généré automatiquement"
                />
              </Form.Group>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowUserModal(false)}>
              Annuler
            </Button>
            <Button type="submit" variant="danger" disabled={userLoading}>
              {userLoading ? (
                <Spinner animation="border" size="sm" />
              ) : selectedItem ? (
                "Modifier"
              ) : (
                "Créer"
              )}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      <Modal
        show={showOrderModal}
        onHide={() => setShowOrderModal(false)}
        size="lg"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>🛍️ Commande {selectedItem?.order_number}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedItem && (
            <>
              <Row className="mb-3">
                <Col md={6}>
                  <p>
                    <strong>
                      <FiUser className="me-1" /> Client :
                    </strong>{" "}
                    {selectedItem.shipping_name}
                  </p>
                  <p>
                    <strong>
                      <FiMail className="me-1" /> Email :
                    </strong>{" "}
                    {selectedItem.client_email}
                  </p>
                  <p>
                    <strong>
                      <FiPhone className="me-1" /> Tél :
                    </strong>{" "}
                    {selectedItem.shipping_phone}
                  </p>
                </Col>
                <Col md={6}>
                  <p>
                    <strong>
                      <FiHome className="me-1" /> Adresse :
                    </strong>{" "}
                    {selectedItem.shipping_address}
                  </p>
                  <p>
                    <strong>
                      <FiCalendar className="me-1" /> Date :
                    </strong>{" "}
                    {formatDate(selectedItem.created_at)}
                  </p>
                  <p>
                    <strong>Statut :</strong>{" "}
                    <Badge
                      bg={getOrderStatusBadge(selectedItem.status).variant}
                    >
                      {getOrderStatusBadge(selectedItem.status).text}
                    </Badge>
                  </p>
                </Col>
              </Row>
              <div className="table-responsive">
                <Table bordered size="sm">
                  <thead className="bg-light">
                    <tr>
                      <th>Produit</th>
                      <th>Qté</th>
                      <th>Prix unitaire</th>
                      <th>Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedItem.items?.map((item, i) => (
                      <tr key={i}>
                        <td>{item.name}</td>
                        <td className="text-center">{item.quantity}</td>
                        <td className="text-end">
                          {formatPrice(item.price)} FCFA
                        </td>
                        <td className="text-end">
                          {formatPrice(item.price * item.quantity)} FCFA
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td colSpan="3" className="text-end fw-bold">
                        Total
                      </td>
                      <td className="fw-bold text-danger">
                        {formatPrice(selectedItem.total_amount)} FCFA
                      </td>
                    </tr>
                  </tfoot>
                </Table>
              </div>
              {selectedItem.shipment && (
                <Alert variant="info" className="mt-3">
                  <FiPackage className="me-2" />
                  Colis associé :{" "}
                  <strong>{selectedItem.shipment.tracking_number}</strong>
                  <Badge
                    bg={
                      getStatusBadge(selectedItem.shipment.current_status)
                        .variant
                    }
                    className="ms-2"
                  >
                    {getStatusBadge(selectedItem.shipment.current_status).text}
                  </Badge>
                </Alert>
              )}
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          {selectedItem?.shipment && (
            <Link
              to={`/tracking/${selectedItem.shipment.tracking_number}`}
              target="_blank"
              className="btn btn-outline-primary"
            >
              📍 Suivre le colis
            </Link>
          )}
          <Button variant="secondary" onClick={() => setShowOrderModal(false)}>
            Fermer
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showWhatsappModal}
        onHide={() => setShowWhatsappModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {selectedItem ? "✏️ Modifier" : "➕ Ajouter"} une configuration
            WhatsApp
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleWhatsappSubmit}>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>
                Numéro WhatsApp <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                value={whatsappForm.phone_number}
                onChange={(e) =>
                  setWhatsappForm({
                    ...whatsappForm,
                    phone_number: e.target.value,
                  })
                }
                placeholder="+237612345678"
                required
              />
              <Form.Text className="text-muted">
                Format international: +237XXXXXXXXX
              </Form.Text>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Libellé (optionnel)</Form.Label>
              <Form.Control
                value={whatsappForm.label}
                onChange={(e) =>
                  setWhatsappForm({ ...whatsappForm, label: e.target.value })
                }
                placeholder="Support client, Commercial, etc."
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Check
                type="checkbox"
                label="Définir comme numéro par défaut"
                checked={whatsappForm.is_default}
                onChange={(e) =>
                  setWhatsappForm({
                    ...whatsappForm,
                    is_default: e.target.checked,
                  })
                }
              />
              <Form.Text className="text-muted">
                Le numéro par défaut sera utilisé pour les envois automatiques
              </Form.Text>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Check
                type="checkbox"
                label="Actif"
                checked={whatsappForm.is_active}
                onChange={(e) =>
                  setWhatsappForm({
                    ...whatsappForm,
                    is_active: e.target.checked,
                  })
                }
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="secondary"
              onClick={() => setShowWhatsappModal(false)}
            >
              Annuler
            </Button>
            <Button type="submit" variant="danger">
              {selectedItem ? "Modifier" : "Ajouter"}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      <Modal
        show={showReportModal}
        onHide={() => setShowReportModal(false)}
        size="lg"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>
            📊 Aperçu du rapport -{" "}
            {reportPeriod === "day"
              ? "Aujourd'hui"
              : reportPeriod === "week"
                ? "Cette semaine"
                : reportPeriod === "month"
                  ? "Ce mois"
                  : reportPeriod === "quarter"
                    ? "Ce trimestre"
                    : "Cette année"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Row>
            <Col md={6}>
              <h6>📦 Colis</h6>
              <div className="bg-light p-3 rounded-3">
                <div className="d-flex justify-content-between">
                  <span>Total :</span>
                  <strong>{stats.totalParcels}</strong>
                </div>
                <div className="d-flex justify-content-between">
                  <span>Livrés :</span>
                  <strong className="text-success">{stats.delivered}</strong>
                </div>
                <div className="d-flex justify-content-between">
                  <span>En transit :</span>
                  <strong className="text-primary">{stats.inTransit}</strong>
                </div>
                <div className="d-flex justify-content-between">
                  <span>Incidents :</span>
                  <strong className="text-danger">{stats.incident}</strong>
                </div>
                <div className="d-flex justify-content-between mt-2">
                  <span>CA Colis :</span>
                  <strong className="text-danger">
                    {stats.totalRevenue.toLocaleString()} FCFA
                  </strong>
                </div>
              </div>
            </Col>
            <Col md={6}>
              <h6>🛍️ Store</h6>
              <div className="bg-light p-3 rounded-3">
                <div className="d-flex justify-content-between">
                  <span>Commandes :</span>
                  <strong>{stats.totalOrders}</strong>
                </div>
                <div className="d-flex justify-content-between">
                  <span>Validées :</span>
                  <strong className="text-success">
                    {stats.validatedOrders}
                  </strong>
                </div>
                <div className="d-flex justify-content-between">
                  <span>En attente :</span>
                  <strong className="text-warning">
                    {allOrders.filter((o) => o.status === "pending").length}
                  </strong>
                </div>
                <div className="d-flex justify-content-between mt-2">
                  <span>CA Store :</span>
                  <strong className="text-info">
                    {stats.storeRevenue.toLocaleString()} FCFA
                  </strong>
                </div>
              </div>
            </Col>
          </Row>
          <Row className="mt-3">
            <Col md={6}>
              <h6>👥 Ressources</h6>
              <div className="bg-light p-3 rounded-3">
                <div className="d-flex justify-content-between">
                  <span>Opérateurs :</span>
                  <strong>{stats.operators}</strong>
                </div>
                <div className="d-flex justify-content-between">
                  <span>Administrateurs :</span>
                  <strong>{stats.admins}</strong>
                </div>
                <div className="d-flex justify-content-between">
                  <span>Clients :</span>
                  <strong>{stats.clients}</strong>
                </div>
              </div>
            </Col>
            <Col md={6}>
              <h6>⏱️ Performance</h6>
              <div className="bg-light p-3 rounded-3">
                <div className="d-flex justify-content-between">
                  <span>Délai moyen :</span>
                  <strong>{stats.avgDeliveryDays} jours</strong>
                </div>
              </div>
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowReportModal(false)}>
            Fermer
          </Button>
          <Button
            variant="success"
            onClick={() => {
              setShowReportModal(false);
              handleExport("report", "pdf");
            }}
          >
            <FiDownload /> PDF
          </Button>
          <Button
            variant="success"
            onClick={() => {
              setShowReportModal(false);
              handleExport("report", "xlsx");
            }}
          >
            <FiDownload /> Excel
          </Button>
        </Modal.Footer>
      </Modal>

      <style>{`
                .admin-dashboard {
                    background: #F8FAFC;
                    min-height: 100vh;
                }
                
                .admin-dashboard.dark-theme {
                    background: #0F172A;
                }
                
                .admin-dashboard.dark-theme .bg-white,
                .admin-dashboard.dark-theme .card {
                    background: #1E293B !important;
                    color: #E2E8F0;
                }
                
                .admin-dashboard.dark-theme .bg-light {
                    background: #334155 !important;
                }
                
                .admin-dashboard.dark-theme .text-muted {
                    color: #94A3B8 !important;
                }
                
                .admin-dashboard.dark-theme .table {
                    color: #E2E8F0;
                }
                
                .admin-dashboard.dark-theme .table-light {
                    background: #334155;
                }
                
                .dashboard-header h1 {
                    font-size: 2rem;
                }
                
                .custom-tabs {
                    border-bottom: 2px solid #E2E8F0;
                }
                
                .dark-theme .custom-tabs {
                    border-bottom-color: #334155;
                }
                
                .custom-tabs .nav-link {
                    color: #64748B;
                    font-weight: 500;
                    padding: 12px 24px;
                    border: none;
                    border-radius: 12px 12px 0 0;
                    margin-right: 4px;
                    transition: all 0.2s;
                }
                
                .custom-tabs .nav-link:hover {
                    color: ${CHART_COLORS.primary};
                    background: rgba(198, 40, 40, 0.05);
                }
                
                .custom-tabs .nav-link.active {
                    color: ${CHART_COLORS.primary};
                    background: white;
                    border-bottom: 3px solid ${CHART_COLORS.primary};
                }
                
                .dark-theme .custom-tabs .nav-link.active {
                    background: #1E293B;
                }
                
                .stat-card-modern {
                    border-radius: 20px;
                    transition: all 0.3s ease;
                }
                
                .stat-icon-wrapper {
                    width: 56px;
                    height: 56px;
                    border-radius: 16px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                }
                
                .stat-value {
                    font-size: 28px;
                    font-weight: 700;
                    line-height: 1.2;
                }
                
                .stat-label {
                    font-size: 13px;
                }
                
                .spin {
                    animation: spin 1s linear infinite;
                }
                
                @keyframes spin {
                    from { transform: rotate(0deg); }
                    to { transform: rotate(360deg); }
                }
                
                @media (max-width: 768px) {
                    .dashboard-header h1 { font-size: 1.5rem; }
                    .custom-tabs .nav-link { padding: 10px 12px; font-size: 13px; }
                    .stat-value { font-size: 22px; }
                }
            `}</style>
    </div>
  );
};

export default AdminDashboard;
